
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import {
  Firestore, getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
  memoryLocalCache,
  collection, doc, setDoc, getDoc, getDocs, updateDoc,
  deleteDoc, query, where, serverTimestamp,
  arrayUnion, arrayRemove, onSnapshot, orderBy, writeBatch, limit,
  Timestamp, increment
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getAuth, initializeAuth, signInWithPopup, GoogleAuthProvider, signOut,
  createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, signInWithCredential,
  signInWithCustomToken, onAuthStateChanged, User, UserCredential,
  OAuthProvider,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  browserLocalPersistence, indexedDBLocalPersistence
} from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { Browser } from "@capacitor/browser";
import { CampingSpot, GpxRoute, MapBounds, AppUser, DeletionSuggestion, CategorySuggestion, RouteStats, WikiSpot, RouteStatus, RouteDifficulty, RouteRegion, RouteExternalLink, ElevationPoint, TesterRequest, TesterProfile, TesterThread, TesterMessage, CopilotMessage, CopilotTurnResponse } from "../types";
import { getSpotSource, mergeSpotsByIdentity } from "./spotIdentity";
import { normalizeCachedSpotDoc } from "./cachedSpotDoc";
import { mergeSpotDocs } from "./spotFetchMerge";
import { sortSpotImagesUserFirst } from "./spotImageSort";
import { isSyntheticSpotId } from "./waypointService";
import { compressImages, compressImagesDetailed, makeThumbnail, ensureCompressedImage, UnsendableImagesError } from "../utils/imageCompression";
import { detectCountry } from "../utils/countryDetection";
import { snapBoundsOutward, cacheGridForZoom } from "../utils/boundsUtils";
import { selectGeocellQuery, geocellFields } from "../utils/geocell";
import { APP_URL } from "../constants";
import i18n from "../i18n";

// Safe environment variable retrieval
const getEnv = (key: string) => {
  try {
    // @ts-ignore
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // @ts-ignore
      return import.meta.env[key];
    }
  } catch (e) { }

  try {
    // @ts-ignore
    if (typeof process !== 'undefined' && process.env) {
      // @ts-ignore
      return process.env[key];
    }
  } catch (e) { }

  return undefined;
};

// Firebase configuration with hardcoded fallbacks
const firebaseConfig = {
  apiKey: getEnv('VITE_FIREBASE_API_KEY') || "AIzaSyBkPcKPs_0nhOsHi7wGwBsSRDcCPOMvO-o",
  authDomain: getEnv('VITE_FIREBASE_AUTH_DOMAIN') || "fricamp-2810e.firebaseapp.com",
  projectId: getEnv('VITE_FIREBASE_PROJECT_ID') || "fricamp-2810e",
  storageBucket: getEnv('VITE_FIREBASE_STORAGE_BUCKET') || "fricamp-2810e.firebasestorage.app",
  messagingSenderId: getEnv('VITE_FIREBASE_MESSAGING_SENDER_ID') || "1081345513013",
  appId: getEnv('VITE_FIREBASE_APP_ID') || "1:1081345513013:web:15cbd922ce9b61e2fb437c",
  measurementId: "G-NV064ZP39N"
};

export const app = initializeApp(firebaseConfig);

// Firebase App Check — kun web for nå (reCAPTCHA Enterprise).
// Native (iOS/Android) hopper vi over inntil vi kan teste det grundig på ekte enheter.
// Play Integrity API krever Play Store, og Google sin AAB-pre-launch-test bruker enheter
// uten Play Store → Firestore-queries henger på App Check-token og "content fails to load".
// Native App Check kan re-aktiveres senere når enforcement er klart og vi har testet kjøremiljø.
try {
  const isNative = Capacitor.isNativePlatform();
  if (!isNative && typeof window !== 'undefined') {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider('6LdprsgsAAAAAH2kIw44Y02lJI94l6yz4szD8v8v'),
      isTokenAutoRefreshEnabled: true
    });
  }
} catch (e) {
  console.warn('App Check init failed:', e);
}

// Firestore cache strategy:
// - Native iOS: memoryLocalCache to avoid IndexedDB hangs in WKWebView
// - Web: memoryLocalCache to avoid localStorage/IndexedDB quota crashes from listener-heavy views
// - Native Android: persistentLocalCache for offline support
let db_instance: Firestore;
try {
  const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const isWeb = !Capacitor.isNativePlatform();
  if (isIOSNative || isWeb) {
    db_instance = initializeFirestore(app, {
      localCache: memoryLocalCache()
    });
  } else {
    db_instance = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    });
  }
} catch {
  db_instance = getFirestore(app);
}
export const db = db_instance;

// Auth: On iOS native, use initializeAuth with browserLocalPersistence (localStorage)
// from the start. getAuth() defaults to IndexedDB persistence, which hangs silently
// in WKWebView on newer iOS versions (17+). Using initializeAuth avoids touching
// IndexedDB entirely — no migration, no hang.
const isIOSNativeAuth = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
export const auth = isIOSNativeAuth
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app);
export const storage = getStorage(app);
export const analytics = getAnalytics(app);
export const functions = getFunctions(app);

export let messaging: ReturnType<typeof getMessaging> | null = null;
if (!Capacitor.isNativePlatform()) {
  isMessagingSupported()
    .then(supported => { if (supported) messaging = getMessaging(app); })
    .catch(() => {});
}

export const getAiPrompt = () => {
  return i18n.t('firebase.ai_prompt');
};

export const getDefaultAiPrompt = () => i18n.t('firebase.ai_prompt');

/** @deprecated Use getDefaultAiPrompt() instead */
export const DEFAULT_AI_PROMPT = i18n.t('firebase.ai_prompt');

export let lastFirebaseDebug = {
  rawCount: 0,
  queryCount: 0
};

const FIREBASE_VERBOSE_LOGS = getEnv('VITE_FIREBASE_VERBOSE_LOGS') === 'true';

const firebaseDebugLog = (...args: unknown[]) => {
  if (FIREBASE_VERBOSE_LOGS) console.log(...args);
};

// --- HELPER: Sanitize data for Firestore ---
// Removes undefined values recursively, as Firestore does not support them.
const cleanFirestoreData = (data: any): any => {
  if (data === null || data === undefined) return null; // Convert undefined/null to null, or strip if key logic used
  if (typeof data !== 'object') return data;
  if (data instanceof Date) return data;
  // Preserve Firestore FieldValue sentinels (serverTimestamp, increment, arrayUnion, etc.)
  if (data._methodName || data.type === 'AggregateField') return data;

  if (Array.isArray(data)) {
    return data.map(item => cleanFirestoreData(item)).filter(item => item !== undefined);
  }

  const result: any = {};
  for (const key in data) {
    const value = data[key];
    if (value !== undefined) {
      result[key] = cleanFirestoreData(value);
    }
  }
  return result;
};

const STORAGE_URL_RE = /^(https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^\/]+\/o\/)([^?]+)/;

export const getThumbUrl = (originalUrl: string): string => {
  if (!originalUrl) return originalUrl;
  const m = originalUrl.match(STORAGE_URL_RE);
  if (!m) return originalUrl;
  const decodedPath = decodeURIComponent(m[2]);
  if (!decodedPath.startsWith('spots/') && !decodedPath.startsWith('reviews/') && !decodedPath.startsWith('articles/')) {
    return originalUrl;
  }
  const thumbPath = encodeURIComponent(`thumbs/${decodedPath}`);
  return `${m[1]}${thumbPath}?alt=media`;
};

/** Storage-stien (ikke URL-en) en miniatyr skal ligge på. */
export const thumbStoragePath = (originalPath: string): string => `thumbs/${originalPath}`;

/**
 * Skriv en miniatyr ved siden av et nettopp opplastet bilde. Best-effort: feiler
 * den, faller visningene tilbake til originalen som før (getThumbUrl-URL-en gir
 * 404/403 og img-ens onError bytter til original-URL-en).
 */
export const uploadThumbFor = async (originalStoragePath: string, file: File): Promise<void> => {
  try {
    const thumb = await makeThumbnail(file);
    if (!thumb) return;
    await uploadBytes(ref(storage, thumbStoragePath(originalStoragePath)), thumb, { contentType: 'image/jpeg' });
  } catch (e) {
    console.warn('[uploadThumbFor] miniatyr ikke lagret:', e);
  }
};

// ── Google Sign-In via PKCE + Chrome Custom Tab ───────────────────────────────
const GOOGLE_ANDROID_CLIENT_ID = '1081345513013-dlcu8tqvfmc69k4r55rntlh6v4sv193i.apps.googleusercontent.com';
const GOOGLE_REDIRECT_URI = 'https://fricamp-2810e.web.app/auth-callback.html';

function generateCodeVerifier(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Auth Functions
export const deleteMyAccount = async () => {
  const callable = httpsCallable(functions, 'deleteMyAccountV2');
  return callable();
};

export const sendStickerBoardEmail = async (pdfBase64: string, email?: string) => {
  const callable = httpsCallable<{ pdfBase64: string; email?: string }, { success: boolean }>(functions, 'sendStickerBoardEmail');
  const result = await callable({ pdfBase64, email });
  return result.data;
};

export const handleGoogleLogin = async (): Promise<UserCredential | void> => {
  const platform = Capacitor.getPlatform();

  if (platform === 'ios') {
    const result = await FirebaseAuthentication.signInWithGoogle();
    const idToken = result.credential?.idToken;
    const accessToken = result.credential?.accessToken;
    if (!idToken) throw new Error(i18n.t('auth.errors.google_no_token', 'No credential received from Google'));
    const credential = GoogleAuthProvider.credential(idToken, accessToken);
    const userCred = await signInWithCredential(auth, credential);

    const userDocRef = doc(db, 'users', userCred.user.uid);
    (async () => {
      const userDocSnap = await getDoc(userDocRef);
      const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
      const updates: any = {};
      if (!existingData.photoURL && userCred.user.photoURL) updates.photoURL = userCred.user.photoURL;
      if (!existingData.platform) updates.platform = platform;
      if (!existingData.creationTimestamp) {
        updates.creationTimestamp = userCred.user.metadata.creationTime
          ? new Date(userCred.user.metadata.creationTime).getTime()
          : Date.now();
      }
      if (Object.keys(updates).length > 0) {
        await setDoc(userDocRef, updates, { merge: true });
      }
    })().catch(e => console.warn('Google Sign-In (ios): metadata save failed:', e));

    return userCred;
  }

  if (platform === 'android') {
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    localStorage.setItem('google_pkce_verifier', codeVerifier);

    const params = new URLSearchParams({
      client_id: GOOGLE_ANDROID_CLIENT_ID,
      redirect_uri: GOOGLE_REDIRECT_URI,
      response_type: 'code',
      scope: 'openid email profile',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      prompt: 'select_account',
    });

    await Browser.open({ url: `https://accounts.google.com/o/oauth2/v2/auth?${params}` });
    return;
  }

  const provider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, provider);

  try {
    const userDocRef = doc(db, 'users', result.user.uid);
    const userDocSnap = await getDoc(userDocRef);
    const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
    const updates: any = {};
    if (!existingData.photoURL && result.user.photoURL) updates.photoURL = result.user.photoURL;
    if (!existingData.platform) updates.platform = Capacitor.getPlatform();
    if (!existingData.creationTimestamp) {
      updates.creationTimestamp = result.user.metadata.creationTime
        ? new Date(result.user.metadata.creationTime).getTime()
        : Date.now();
    }
    if (Object.keys(updates).length > 0) {
      await setDoc(userDocRef, updates, { merge: true });
    }
  } catch (e) {
    console.warn('Google Sign-In: failed to save profile metadata:', e);
  }

  return result;
};

/** Called from App.tsx when the OAuth redirect deep link arrives. Throws on failure. */
export const processGoogleRedirectResult = async (url: string): Promise<void> => {
  const queryString = url.includes('?') ? url.split('?')[1] : '';
  const code = new URLSearchParams(queryString).get('code');
  if (!code) return;

  const codeVerifier = localStorage.getItem('google_pkce_verifier');
  if (!codeVerifier) throw new Error(i18n.t('firebase.errors.pkce_missing'));
  localStorage.removeItem('google_pkce_verifier');

  const fns = getFunctions(app, 'us-central1');
  const googleSignIn = httpsCallable<{ code: string; codeVerifier: string }, { customToken: string }>(fns, 'googleSignInWithToken');
  const result = await googleSignIn({ code, codeVerifier });
  await signInWithCustomToken(auth, result.data.customToken);
  await Browser.close().catch(() => { });
};

// ── Apple Sign-In ──────────────────────────────────────────────────────────
const APPLE_SERVICE_ID = 'com.takleconsulting.fricamp.service';
const APPLE_CALLBACK_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/appleAuthCallback';

export const handleAppleLogin = async (): Promise<UserCredential | void> => {
  const platform = Capacitor.getPlatform();

  if (platform === 'ios') {
    const result = await Promise.race([
      FirebaseAuthentication.signInWithApple(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(i18n.t('auth.errors.apple_timeout', 'Sign in with Apple timed out. Please try again.'))), 60000)
      ),
    ]);

    const idToken = result.credential?.idToken;
    const rawNonce = result.credential?.nonce;
    if (!idToken) throw new Error(i18n.t('auth.errors.apple_no_id_token', 'No id_token received from Apple'));

    const provider = new OAuthProvider('apple.com');
    const oauthCredential = provider.credential({ idToken, rawNonce: rawNonce || undefined });
    const userCred = await signInWithCredential(auth, oauthCredential);

    const displayName = result.user?.displayName;
    const userDocRef = doc(db, 'users', userCred.user.uid);
    (async () => {
      const userDocSnap = await getDoc(userDocRef);
      const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
      const updates: any = {};
      if (displayName && !existingData.displayName) updates.displayName = displayName;
      if (!existingData.platform) updates.platform = platform;
      if (!existingData.creationTimestamp) {
        updates.creationTimestamp = userCred.user.metadata.creationTime
          ? new Date(userCred.user.metadata.creationTime).getTime()
          : Date.now();
      }
      if (Object.keys(updates).length > 0) {
        await setDoc(userDocRef, updates, { merge: true });
      }
    })().catch((e) => console.warn('Apple Sign-In (ios): metadata save failed:', e));

    return userCred;
  }

  if (platform === 'android') {
    const nonce = generateCodeVerifier();
    const hashedNonce = await generateCodeChallenge(nonce);
    localStorage.setItem('apple_auth_nonce', nonce);

    const params = new URLSearchParams({
      client_id: APPLE_SERVICE_ID,
      redirect_uri: APPLE_CALLBACK_URL,
      response_type: 'code',
      response_mode: 'form_post',
      scope: 'name email',
      state: hashedNonce,
    });

    await Browser.open({ url: `https://appleid.apple.com/auth/authorize?${params}` });
    return;
  }

  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  const result = await signInWithPopup(auth, provider);

  try {
    const userDocRef = doc(db, 'users', result.user.uid);
    const userDocSnap = await getDoc(userDocRef);
    const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
    const updates: any = {};
    if (!existingData.platform) updates.platform = Capacitor.getPlatform();
    if (!existingData.creationTimestamp) {
      updates.creationTimestamp = result.user.metadata.creationTime
        ? new Date(result.user.metadata.creationTime).getTime()
        : Date.now();
    }
    if (Object.keys(updates).length > 0) {
      await setDoc(userDocRef, updates, { merge: true });
    }
  } catch (e) {
    console.warn('Apple Sign-In (web): failed to save profile metadata:', e);
  }

  return result;
};

/** Called from App.tsx when the Apple OAuth deep link arrives (Android only). Throws on failure. */
export const processAppleRedirectResult = async (url: string): Promise<void> => {
  const queryString = url.includes('?') ? url.split('?')[1] : '';
  const params = new URLSearchParams(queryString);
  const idToken = params.get('id_token');
  const name = params.get('name');

  if (!idToken) throw new Error(i18n.t('auth.errors.apple_no_id_token', 'No id_token received from Apple'));

  const storedNonce = localStorage.getItem('apple_auth_nonce');
  localStorage.removeItem('apple_auth_nonce');

  const provider = new OAuthProvider('apple.com');
  const oauthCredential = provider.credential({
    idToken,
    rawNonce: storedNonce || undefined,
  });

  const userCred = await signInWithCredential(auth, oauthCredential);

  // Save Apple-provided name to Firestore (only available on first sign-in)
  if (name) {
    const decodedName = decodeURIComponent(name);
    if (decodedName) {
      const userDocRef = doc(db, 'users', userCred.user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
      if (!existingData.displayName) {
        await setDoc(userDocRef, { displayName: decodedName }, { merge: true });
        try { await updateProfile(userCred.user, { displayName: decodedName }); } catch (_) { }
      }
    }
  }

  await Browser.close().catch(() => { });
};

export const registerUser = (email: string, password: string) => {
  return createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
};

export const loginUser = (email: string, password: string) => {
  return signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
};

export const sendPasswordResetEmail = (email: string) => {
  return firebaseSendPasswordResetEmail(auth, email.trim().toLowerCase());
};

export const logoutUser = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await FirebaseAuthentication.signOut();
    } catch (_) {
      // Ignore native logout errors and still clear Firebase JS session.
    }
  }
  return signOut(auth);
};

export const updateUserProfile = (user: User, updates: { displayName?: string; photoURL?: string }) => {
  return updateProfile(user, updates);
};

export const updateUserSettings = async (
  uid: string,
  settings: Partial<AppUser>
) => {
  const userRef = doc(db, "users", uid);
  await setDoc(userRef, cleanFirestoreData(settings), { merge: true });
};

export const updateFcmToken = async (uid: string, token: string | null) => {
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { fcmToken: token }, { merge: true });
};

export const enableNotifications = async (uid: string, enabled: boolean) => {
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { notificationsEnabled: enabled }, { merge: true });
};

export const subscribeToAuth = (callback: (user: AppUser | null) => void) => {
  let unsubUserDoc: (() => void) | null = null;
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (unsubUserDoc) {
      unsubUserDoc();
      unsubUserDoc = null;
    }

    firebaseDebugLog('[AUTH] onAuthStateChanged fired. user:', firebaseUser?.uid || 'null');
    if (firebaseUser) {
      let tokenIsAdmin = false;
      try {
        const tokenResult = await firebaseUser.getIdTokenResult(true);
        const claims: any = tokenResult?.claims || {};
        const claimRole = String(claims.role || '').toLowerCase();
        const claimRoles = Array.isArray(claims.roles) ? claims.roles.map((r: any) => String(r).toLowerCase()) : [];
        tokenIsAdmin =
          claims.admin === true ||
          claims.isAdmin === true ||
          claimRole === 'admin' ||
          claimRoles.includes('admin');
      } catch (tokenErr: any) {
        // If token refresh fails completely, this is likely an orphaned/zombie auth session
        // (user deleted from Auth but session cached in IndexedDB). Force sign out.
        console.warn('[AUTH] Token refresh failed — signing out orphaned session:', tokenErr?.code || tokenErr?.message);
        try { await auth.signOut(); } catch {}
        callback(null);
        return;
      }

      // Fire preliminary callback so the app can render immediately (before Firestore responds)
      firebaseDebugLog('[AUTH] Preliminary callback firing for uid:', firebaseUser.uid);
      callback({
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        isEVUser: false,
        vehicleType: 'standard',
        offroadRoadFilters: { track: false, unclassified: false, service: false, residential: false },
        subscriptionStatus: undefined,
        isTester: false,
        isAdmin: tokenIsAdmin || ['jonborgentakle@gmail.com', 'dicpnorway@gmail.com'].includes((firebaseUser.email || '').toLowerCase()),
        adminClaim: tokenIsAdmin,
        _firestoreReady: false,
      });

      // Timeout: if onSnapshot hasn't fired within 8 seconds (e.g. Firestore SDK
      // issues on iOS), promote the preliminary callback to _firestoreReady: true
      // so the app remains usable. The server-side trigger guarantees the doc exists.
      let snapshotFired = false;
      const firestoreTimeout = setTimeout(() => {
        if (!snapshotFired) {
          console.warn('[AUTH] onSnapshot timeout — promoting preliminary callback to firestoreReady');
          callback({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isEVUser: false,
            vehicleType: 'standard',
            offroadRoadFilters: { track: false, unclassified: false, service: false, residential: false },
            subscriptionStatus: 'free',
            isTester: false,
            // Behold eier-admin selv når Firestore-snapshot ikke rakk å fyre (timeout).
            // Ellers mister eieren admin (og dermed finland/nordic-gating) hvis
            // f.eks. nettleserens tracking-prevention bremser Firestore-persistens.
            isAdmin: tokenIsAdmin || ['jonborgentakle@gmail.com', 'dicpnorway@gmail.com'].includes((firebaseUser.email || '').toLowerCase()),
            adminClaim: tokenIsAdmin,
            _firestoreReady: true,
          });
        }
      }, 8000);

      // Keep user roles/settings in sync while user is logged in.
      unsubUserDoc = onSnapshot(
        doc(db, "users", firebaseUser.uid),
        (userDoc) => {
          snapshotFired = true;
          clearTimeout(firestoreTimeout);
          const userData = userDoc.data();
          firebaseDebugLog('[AUTH] onSnapshot fired. exists:', userDoc.exists(), 'subscriptionStatus:', userData?.subscriptionStatus, 'isAdmin:', userData?.isAdmin, 'tokenIsAdmin:', tokenIsAdmin, '_firestoreReady: true');
          const platform = Capacitor.getPlatform();
          const creationTimestamp = firebaseUser.metadata.creationTime
            ? new Date(firebaseUser.metadata.creationTime).getTime()
            : Date.now();
          const userRole = String(userData?.role || '').toLowerCase();
          const userRoles = Array.isArray(userData?.roles) ? userData.roles.map((r: any) => String(r).toLowerCase()) : [];
          const userDocIsAdmin = !!userData?.isAdmin || userRole === 'admin' || userRoles.includes('admin');

          // Auto-create profile doc for new users (covers all sign-in methods)
          if (!userData) {
            setDoc(doc(db, "users", firebaseUser.uid), {
              displayName: firebaseUser.displayName || null,
              email: firebaseUser.email || null,
              photoURL: firebaseUser.photoURL || null,
              platform,
              creationTimestamp,
              subscriptionStatus: 'free',
            }, { merge: true }).catch((e) => console.warn('Failed to create user profile:', e));
          }

          // Backfill metrics if missing on existing doc
          if (userData && (!userData.platform || !userData.creationTimestamp || !userData.email || !userData.displayName)) {
            const backfill: any = {};
            if (!userData.platform) backfill.platform = platform;
            if (!userData.creationTimestamp) backfill.creationTimestamp = creationTimestamp;
            if (!userData.email && firebaseUser.email) backfill.email = firebaseUser.email;
            if (!userData.displayName && firebaseUser.displayName) backfill.displayName = firebaseUser.displayName;
            if (Object.keys(backfill).length > 0) {
              updateDoc(doc(db, "users", firebaseUser.uid), backfill).catch(() => { });
            }
          }

          // Increment loginCount once per session (sessionStorage guard)
          // Samtidig: oppdater lastLoginAt + legg uka til activeWeeks (for unike aktive brukere per uke)
          if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem('fricamp_session_counted')) {
            sessionStorage.setItem('fricamp_session_counted', '1');
            const now = new Date();
            const utc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
            utc.setUTCDate(utc.getUTCDate() + 4 - (utc.getUTCDay() || 7));
            const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
            const isoWeek = Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
            const weekKey = `${utc.getUTCFullYear()}-W${String(isoWeek).padStart(2, '0')}`;
            updateDoc(doc(db, "users", firebaseUser.uid), {
              loginCount: increment(1),
              lastLoginAt: Date.now(),
              activeWeeks: arrayUnion(weekKey),
            }).catch(() => { });
          }

          // Auto-resync vehicle fields if activeVehicleId exists but newer denormalized fields are missing
          if (
            userData?.activeVehicleId &&
            userData.vehicleMarkerColor === undefined &&
            typeof sessionStorage !== 'undefined' &&
            !sessionStorage.getItem('fricamp_vehicle_resynced')
          ) {
            sessionStorage.setItem('fricamp_vehicle_resynced', '1');
            firebaseDebugLog('[AUTH] Auto-resyncing vehicle fields (vehicleMarkerColor missing)');
            import('./vehicleService').then(({ resyncDefaultVehicle }) => {
              resyncDefaultVehicle(firebaseUser.uid).catch(e =>
                console.warn('[AUTH] Vehicle resync failed:', e)
              );
            }).catch(() => {});
          }

          // Active vehicle derivation (backward-compatible)
          const hasActiveVehicle = !!userData?.activeVehicleId;
          const derivedVehicleType = (() => {
            if (!hasActiveVehicle) return (userData?.vehicleType || 'standard') as 'standard' | 'offroad';
            const cat = userData?.activeVehicleCategory;
            const has4wd = !!userData?.activeVehicleEquipment?.four_wheel_drive;
            const terrainPrefs: string[] = userData?.activeVehicleTerrainPrefs || [];
            const hasOffroadTerrain = terrainPrefs.some((t: string) => ['forest', 'mountain', 'sand', 'snow'].includes(t));
            if (cat === 'rooftent' || has4wd || hasOffroadTerrain) return 'offroad' as const;
            return 'standard' as const;
          })();
          const derivedIsEV = hasActiveVehicle
            ? !!userData?.activeVehicleEquipment?.is_ev
            : !!userData?.isEVUser;
          const derivedNeedsEmptying = hasActiveVehicle
            ? !!(userData?.activeVehicleEquipment?.fixed_toilet || userData?.activeVehicleEquipment?.grey_water_tank)
            : !!userData?.needsEmptying;

          callback({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: userData?.displayName || firebaseUser.displayName,
            photoURL: userData?.photoURL || firebaseUser.photoURL,
            isEVUser: derivedIsEV,
            vehicleType: derivedVehicleType,
            offroadRoadFilters: (() => {
              // Priority: vehicle road_filters > legacy offroadRoadFilters
              if (hasActiveVehicle && userData?.activeVehicleRoadFilters) {
                return {
                  track: !!userData.activeVehicleRoadFilters.track,
                  unclassified: !!userData.activeVehicleRoadFilters.unclassified,
                  service: !!userData.activeVehicleRoadFilters.service,
                  residential: !!userData.activeVehicleRoadFilters.residential,
                };
              }
              // Fallback: legacy profile-level filters (default false for new users)
              return {
                track: !!userData?.offroadRoadFilters?.track,
                unclassified: !!userData?.offroadRoadFilters?.unclassified,
                service: !!userData?.offroadRoadFilters?.service,
                residential: !!userData?.offroadRoadFilters?.residential,
              };
            })(),
            subscriptionStatus: userData?.subscriptionStatus,
            subscriptionProvider: userData?.subscriptionProvider,
            subscriptionId: userData?.subscriptionId,
            stripeCustomerId: userData?.stripeCustomerId,
            vippsAgreementId: userData?.vippsAgreementId,
            currentPeriodEnd: userData?.currentPeriodEnd,
            paidUntil: userData?.paidUntil,
            isTester: !!userData?.isTester,
            isAdmin: userDocIsAdmin || tokenIsAdmin || ['jonborgentakle@gmail.com', 'dicpnorway@gmail.com'].includes((firebaseUser.email || '').toLowerCase()),
            adminClaim: tokenIsAdmin,
            role: userData?.role,
            roles: Array.isArray(userData?.roles) ? userData.roles : undefined,
            language: userData?.language,
            fcmToken: userData?.fcmToken ?? null,
            notificationsEnabled: !!userData?.notificationsEnabled,
            tier: userData?.tier || 0,
            totalVisits: userData?.totalVisits || 0,
            creationTimestamp: userData?.creationTimestamp || creationTimestamp,
            platform: userData?.platform || (platform as any),
            campaignsReached: userData?.campaignsReached || [],
            needsEmptying: derivedNeedsEmptying,
            activeVehicleId: userData?.activeVehicleId || undefined,
            activeVehicleCategory: userData?.activeVehicleCategory || undefined,
            activeVehicleLength: userData?.activeVehicleLength || undefined,
            activeVehicleWidth: userData?.activeVehicleWidth || undefined,
            activeVehicleWeight: userData?.activeVehicleWeight || undefined,
            activeVehicleEquipment: userData?.activeVehicleEquipment || undefined,
            activeVehicleTerrainPrefs: userData?.activeVehicleTerrainPrefs || undefined,
            activeVehicleRoadFilters: userData?.activeVehicleRoadFilters || undefined,
            activeVehiclePhotoUrl: userData?.activeVehiclePhotoUrl || undefined,
            activeVehicleAiAvatarSvg: userData?.activeVehicleAiAvatarSvg || undefined,
            activeVehicleHasTow: !!userData?.activeVehicleHasTow,
            vehicleMarkerEnabled: !!userData?.vehicleMarkerEnabled,
            vehicleMarkerColor: userData?.vehicleMarkerColor || undefined,
            onboarding_vehicle_done: !!userData?.onboarding_vehicle_done,
            county_notifications_spots: Array.isArray(userData?.county_notifications_spots) ? userData.county_notifications_spots : [],
            county_notifications_reviews: Array.isArray(userData?.county_notifications_reviews) ? userData.county_notifications_reviews : [],
            homeCounty: userData?.homeCounty || undefined,
            homeCountySlug: userData?.homeCountySlug || undefined,
            homeMunicipality: userData?.homeMunicipality || undefined,
            homeMunicipalitySlug: userData?.homeMunicipalitySlug || undefined,
            homeRegionDetectedAt: userData?.homeRegionDetectedAt || undefined,
            loginCount: userData?.loginCount || 0,
            testflight_opt_in: !!userData?.testflight_opt_in,
            testflight_opt_in_at: userData?.testflight_opt_in_at || undefined,
            testflight_synced: !!userData?.testflight_synced,
            testflight_tester_id: userData?.testflight_tester_id || null,
            testflight_sync_error: userData?.testflight_sync_error || null,
            testflight_last_synced_at: userData?.testflight_last_synced_at || undefined,
            themePreference: userData?.themePreference || undefined,
            mapLayers: userData?.mapLayers || undefined,
            stickerLayout: userData?.stickerLayout || undefined,
            currentCheckIn: userData?.currentCheckIn || undefined,
            _firestoreReady: true,
          });
        },
        (error) => {
          snapshotFired = true;
          clearTimeout(firestoreTimeout);
          console.error('Firestore user doc snapshot error:', error?.code, error?.message);
          callback({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isEVUser: false,
            vehicleType: 'standard',
            offroadRoadFilters: {
              track: false,
              unclassified: false,
              service: false,
              residential: false
            },
            subscriptionStatus: undefined,
            isTester: false,
            // Behold eier-admin selv når user-doc-snapshot FEILER (f.eks. tracking-
            // prevention blokkerer Firestore IndexedDB). Uten dette faller eieren til
            // isAdmin:false → isAdminUser=false → finland/nordic-gating slås av.
            isAdmin: tokenIsAdmin || ['jonborgentakle@gmail.com', 'dicpnorway@gmail.com'].includes((firebaseUser.email || '').toLowerCase()),
            adminClaim: tokenIsAdmin,
            _firestoreReady: true,
          });
        }
      );
    } else {
      callback(null);
    }
  });
};

// System Config
export const fetchSystemConfig = async (key: string) => {
  const docRef = doc(db, "system", key);
  const snapshot = await getDoc(docRef);
  return snapshot.exists() ? snapshot.data() : null;
};

export const saveSystemConfig = async (key: string, data: any) => {
  await setDoc(doc(db, "system", key), cleanFirestoreData(data), { merge: true });
};

export const updateNordicConfig = async (country: string, enabled: boolean): Promise<Record<string, boolean>> => {
  const callable = httpsCallable<{ country: string; enabled: boolean }, {
    ok: boolean;
    config: Record<string, boolean>;
  }>(functions, 'updateNordicConfig');
  const result = await callable({ country, enabled });
  return result.data.config;
};

// Spots Logic — in-memory cache to avoid redundant Firestore queries on small viewport changes
const spotAreaCache = new Map<string, { data: CampingSpot[], expiry: number }>();
const SPOT_CACHE_TTL = 5 * 60 * 1000; // 5 min
const SPOT_CACHE_MAX = 40;
const getSpotCacheKey = (bounds: MapBounds) => {
  // Snap to ~10 km grid (0.1°) for better cache hit rate across small viewport shifts
  const snap = (v: number) => (Math.round(v * 10) / 10).toFixed(2);
  return `${snap(bounds.latMin)}_${snap(bounds.lonMin)}_${snap(bounds.latMax)}_${snap(bounds.lonMax)}`;
};

// Score helper: verified/confirmed spots get highest priority,
// then sort by numeric score descending.
const spotScore = (s: CampingSpot): number => {
  if (s.sourceLevel === 4 || s.isVerified || s.spotType === 'confirmed') return 100;
  if (s.spotType === 'commercial_campsite') return 90;
  return s.aiResult?.score ?? s.score ?? 0;
};

export const computeQualityTier = (spot: CampingSpot): 1 | 2 | 3 => {
  if (spot.sourceLevel === 4 || spot.isVerified || spot.spotType === 'confirmed') return 1;
  if (spot.spotType === 'commercial_campsite') return 2;
  return 3;
};

/**
 * Spatial sampling: keep only the best-scoring spot per grid cell.
 * Grid size adapts to zoom: ~2km at zoom 9, ~1km at 10, ~0.5km at 11.
 */
export const spatialSample = (spots: CampingSpot[], zoom: number): CampingSpot[] => {
  // At zoom > 11, return all spots (no sampling)
  if (zoom > 11) return spots;
  // Grid cell size in degrees (approximate):
  // zoom 9: ~0.02° ≈ 2km, zoom 10: ~0.01° ≈ 1km, zoom 11: ~0.005° ≈ 0.5km
  const gridSize = zoom <= 9 ? 0.02 : zoom <= 10 ? 0.01 : 0.005;
  const cells = new Map<string, CampingSpot>();
  for (const spot of spots) {
    const cellKey = `${Math.floor(spot.location.latitude / gridSize)}_${Math.floor(spot.location.longitude / gridSize)}`;
    const existing = cells.get(cellKey);
    if (!existing || spotScore(spot) > spotScore(existing)) {
      cells.set(cellKey, spot);
    }
  }
  return Array.from(cells.values());
};

/**
 * Fetch slim spot data via Cloud Function with server-side select() projection.
 * Returns only marker-essential fields (~200 bytes/spot vs ~2KB full doc).
 * Used for initial map load to dramatically reduce data transfer.
 */
// Markør-endepunktet nås via Firebase Hosting (/api/spot-markers), ikke via
// funksjonens cloudfunctions.net-URL. Grunnen: Hosting har CDN foran seg, mens
// cloudfunctions.net IKKE har det. Svarets `s-maxage` var derfor uten effekt, og
// hver kartvisning traff funksjonen på nytt — som igjen leste opptil 3000
// Firestore-dokumenter. Se docs/plan-firestore-cost-reduction.md.
//
// Absolutt URL og ikke relativ, fordi appen også kjører i Capacitor der en
// relativ fetch ville gått til den lokale bundlen i stedet for til Hosting.
const SPOT_MARKERS_URL = `${APP_URL}/api/spot-markers`;

// Direkte funksjons-URL beholdes som fallback. Dekker to tilfeller: at rewriten
// ikke er propagert ennå, og at Hosting svarer med SPA-ens index.html (da feiler
// JSON-parsingen og vi faller gjennom hit). Android Auto har denne URL-en
// kompilert inn i native-koden og bruker den uansett.
const SPOT_MARKERS_FALLBACK_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/getSpotMarkers';

export const fetchSlimSpotMarkers = async (bounds: MapBounds, zoom: number): Promise<CampingSpot[]> => {
  // Kvantiser utsnittet slik at nærliggende kartvisninger får identisk URL og
  // deler cache-oppføring. Utvider alltid utover, aldri innover, så ingen
  // plasser kan falle ut av visningen.
  const snapped = snapBoundsOutward(bounds, cacheGridForZoom(zoom));
  const params = new URLSearchParams({
    latMin: snapped.latMin.toFixed(4),
    latMax: snapped.latMax.toFixed(4),
    lonMin: snapped.lonMin.toFixed(4),
    lonMax: snapped.lonMax.toFixed(4),
    zoom: String(Math.round(zoom)),
  });
  const query = `?${params}`;

  let lastErr: unknown = null;
  for (const base of [SPOT_MARKERS_URL, SPOT_MARKERS_FALLBACK_URL]) {
    try {
      const resp = await fetch(`${base}${query}`);
      if (!resp.ok) throw new Error(`getSpotMarkers ${resp.status}`);
      const { spots } = await resp.json();
      return spots as CampingSpot[];
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('getSpotMarkers failed');
};

export interface GetCachedSpotsOptions {
  zoom?: number;
  tierCallback?: (tier1Spots: CampingSpot[]) => void;
}

export const getCachedSpotsInArea = async (bounds: MapBounds, options?: GetCachedSpotsOptions): Promise<CampingSpot[]> => {
  // `options.zoom` leses ikke lenger: spørringen har én form for alle zoom-nivåer,
  // og uttynningen som brukte zoom er fjernet. Feltet står igjen i typen fordi
  // kallstedene sender det, og fordi telemetri kan trenge det senere.
  const tierCallback = options?.tierCallback;

  const cacheKey = getSpotCacheKey(bounds);
  const cached = spotAreaCache.get(cacheKey);
  if (cached && Date.now() < cached.expiry) {
    lastFirebaseDebug.queryCount = cached.data.length;
    return cached.data;
  }

  const spotsRef = collection(db, "cached_spots");

  // Geocelle-spørring i stedet for breddegrad-bånd. Se getSpotMarkers i
  // functions/index.js og docs/plan-firestore-cost-reduction.md for bakgrunnen:
  // range på kun breddegrad tvang lengdegrad-filtrering i minnet (95 % av
  // lesningene kastet), og sortering på breddegrad ga tynne striper med markører
  // i stedet for hele utsnittet. Celle-ID er en likhetsspørring, så vi kan
  // sortere på `score` og få de BESTE plassene i utsnittet.
  const { field, chunks } = selectGeocellQuery(bounds);
  // Sikkerhetstak, ikke tynning — se getSpotMarkers i functions/index.js.
  // Området er alt avgrenset av søkesirkelen (MAX_SEARCH_RADIUS_KM = 50), så
  // innenfor bbox-en leses ALT. Ingen score-rangering: den skjulte hele Danmark,
  // fordi seedede plasser har flat score 5 og aldri får en ekte score.
  // Målt verste 50 km-boks 15.08.2026: Bohuslän 9 134 dokumenter. Taket ligger
  // over det med margin — se getSpotMarkers for hele målingen.
  const AREA_SAFETY_CAP = 12000;
  const limitPerChunk = Math.ceil(AREA_SAFETY_CAP / chunks.length);
  const keepPerQuery = limitPerChunk;

  // Spørringen avgrenser nå begge akser, men normaliseringen kan gi koordinater
  // fra eldre flat-formede dokumenter, så vakten blir stående.
  const inViewport = (s: CampingSpot): boolean =>
    s.location.latitude >= bounds.latMin && s.location.latitude <= bounds.latMax
    && s.location.longitude >= bounds.lonMin && s.location.longitude <= bounds.lonMax;

  // Bevisst UTEN `isEligible`-filter — se begrunnelsen i getSpotMarkers
  // (functions/index.js): bare 0,7 % av dokumentene rammes av 150 m-regelen, så
  // et felt-krav i spørringen ville risikert å skjule nye plasser stille for
  // nesten ingen besparelse.
  //
  // ÉN spørringsform for alle zoom-nivåer: hele bbox-en, begge akser avgrenset i
  // Firestore. Firestore tåler range på flere felt nå — verifisert mot prod
  // 15.08.2026 — så lengdegrad trenger ikke lenger filtreres i minnet etterpå.
  // Krever indeksen `geocell + latitude + longitude` (firestore.indexes.json).
  const queries = chunks.map(cells =>
    query(
      spotsRef,
      where(field, "in", cells),
      where("location.latitude", ">=", bounds.latMin),
      where("location.latitude", "<=", bounds.latMax),
      where("location.longitude", ">=", bounds.lonMin),
      where("location.longitude", "<=", bounds.lonMax),
      limit(limitPerChunk)
    )
  );
  const pending = queries.map(q => getDocs(q));

  // --- Progressiv første tegning ---
  // Den gamle «tier 1»-spørringen hentet qualityTier 1–2 i en EGEN runde med bånd,
  // altså dobbelt antall spørringer for 500 plasser. Unødvendig: vi leverer heller
  // det første delresultatet til kartet med én gang.
  // (Firestore tillater bare ÉN `in` per spørring, så qualityTier-filteret kunne
  // uansett ikke kombineres med celle-filteret.)
  //
  // Første fjerdedel av spørringene, ikke bare den første: hver spørring dekker
  // én celle-gruppe, altså en del av kartet, og ett hjørne alene er ikke en
  // meningsfull førstetegning.
  let tier1Delivered = false;
  if (tierCallback && pending.length > 0) {
    try {
      const førsteBolk = pending.slice(0, Math.max(1, Math.ceil(pending.length / 4)));
      const first = await Promise.all(førsteBolk);
      const firstSpots: CampingSpot[] = [];
      first.forEach(snap => snap.forEach((d: any) => {
        const data = normalizeCachedSpotDoc(d.id, d.data());
        if (!data) return;
        if (!inViewport(data)) return;
        if (!data.isInvalid && !data.isDeleted) firstSpots.push(data);
      }));
      if (firstSpots.length > 0) {
        tierCallback(firstSpots.slice(0, 500));
        tier1Delivered = true;
      }
    } catch (firstErr) {
      // Indeksen kan være under bygging — full spørring under dekker alt.
      console.warn('[getCachedSpotsInArea] progressivt førsteresultat feilet (indeks bygges?):', firstErr);
    }
  }

  // --- Full henting (alle delresultater) ---
  const snapshots = await Promise.all(pending);
  lastFirebaseDebug.rawCount = snapshots.reduce((sum, snap) => sum + snap.size, 0);

  const spots: CampingSpot[] = [];
  snapshots.forEach(snapshot => {
    const chunkSpots: CampingSpot[] = [];
    snapshot.forEach((d: any) => {
      // Normalisering hopper over docs uten gyldig nested location (eldre flat-
      // formede seed-docs) og garanterer `id` (doc.id-fallback for seedede
      // campingplasser uten id-felt — de krasjet rutevisningen).
      const data = normalizeCachedSpotDoc(d.id, d.data());
      if (!data) return;
      if (!inViewport(data)) return;
      if (!data.isInvalid && !data.isDeleted) chunkSpots.push(data);
    });
    // Spørringen sorterer alt på score, men normaliseringen kan ha hoppet over
    // docs, så vi sorterer igjen før vi kapper — ellers kunne kappingen fjernet
    // bedre plasser enn den beholdt.
    chunkSpots.sort((a, b) => spotScore(b) - spotScore(a));
    spots.push(...chunkSpots.slice(0, keepPerQuery));
  });

  // INGEN zoom-basert uttynning. `spatialSample` beholdt tidligere bare den
  // best-scorende plassen per rutenettcelle ved zoom 9–11, altså kastet den data
  // vi allerede hadde lest og betalt for. Kartet klynger markører selv, så
  // tettheten løses ved TEGNING i stedet — se displaySpots i components/Map.tsx.
  const deduped = mergeSpotsByIdentity([], spots);
  lastFirebaseDebug.queryCount = deduped.length;

  // Store in in-memory cache
  spotAreaCache.set(cacheKey, { data: deduped, expiry: Date.now() + SPOT_CACHE_TTL });
  if (spotAreaCache.size > SPOT_CACHE_MAX) {
    const oldest = [...spotAreaCache.entries()].sort((a, b) => a[1].expiry - b[1].expiry)[0];
    spotAreaCache.delete(oldest[0]);
  }

  return deduped;
};

export const fetchSpotsByIds = async (spotIds: string[]): Promise<CampingSpot[]> => {
  const uniqueIds = Array.from(new Set((spotIds || []).map((id) => String(id || '').trim()).filter(Boolean)));
  if (uniqueIds.length === 0) return [];

  const spots: CampingSpot[] = [];
  await Promise.all(uniqueIds.map(async (spotId) => {
    // Les BEGGE kildene og slå dem sammen. For OSM/kommersielle plasser kan
    // `spots/{id}` være en delvis STUB uten `location` — onReviewWrite skriver
    // dit kun denormaliserte ratings (ratingAverage/-Weighted/-Count/-Tier).
    // Leste vi bare `spots` først og returnerte stubben, manglet koordinater, og
    // kart-fly + valg avbrøt stille (`handleSelectSpot` returnerer på
    // `!s.location`) — trykk på «Ny anmeldelse»-varsel «gjorde ingenting».
    // mergeSpotDocs: cached som base (geometri), spots overstyrer (ferske ratings).
    const [mainSnap, cachedSnap] = await Promise.all([
      getDoc(doc(db, "spots", spotId)),
      getDoc(doc(db, "cached_spots", spotId)),
    ]);
    const merged = mergeSpotDocs(
      mainSnap.exists() ? (mainSnap.data() as CampingSpot) : null,
      cachedSnap.exists() ? (cachedSnap.data() as CampingSpot) : null,
      spotId,
    );
    if (merged) spots.push(merged);
  }));

  return spots;
};

/**
 * Fetch the most recently user-verified spots across all collections.
 */
export const fetchRecentVerifiedSpots = async (limitCount = 10): Promise<CampingSpot[]> => {
  try {
    const q = query(
      collection(db, "spots"),
      where("isVerified", "==", true),
      orderBy("verifiedAt", "desc"),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map(d => ({ ...(d.data() as CampingSpot), id: d.id }));
    }
  } catch {
    // Index may not exist yet — fall back without ordering
  }

  // Fallback: get verified spots without ordering
  try {
    const q2 = query(
      collection(db, "spots"),
      where("isVerified", "==", true),
      limit(limitCount)
    );
    const snap2 = await getDocs(q2);
    return snap2.docs.map(d => ({ ...(d.data() as CampingSpot), id: d.id }));
  } catch (e) {
    console.warn("fetchRecentVerifiedSpots failed", e);
    return [];
  }
};

const getSpotNewsTimestamp = (spot: CampingSpot): number => {
  const raw = spot.verifiedAt ?? spot.createdAt ?? 0;
  if (typeof raw === "number") return raw;
  if (typeof (raw as any)?.toMillis === "function") return (raw as any).toMillis();
  if (typeof (raw as any)?.seconds === "number") return (raw as any).seconds * 1000;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const COUNTRY_CODE_TO_CONFIG_KEY: Record<string, string> = { SE: 'sweden', DK: 'denmark', FI: 'finland' };

export const isSpotInEnabledCountry = (
  spot: CampingSpot,
  enabledCountries?: Partial<Record<string, boolean>>
): boolean => {
  if (!enabledCountries || !spot.location) return true;
  const country = detectCountry(spot.location.latitude, spot.location.longitude);
  const configKey = country ? COUNTRY_CODE_TO_CONFIG_KEY[country] : null;
  if (!configKey) return true;
  return enabledCountries[configKey] === true;
};

const isFricampSpot = (s: CampingSpot): boolean =>
  s.spotType === 'confirmed' || s.spotType === 'algorithmic' || s.spotType === 'wildspot';

// Bruker-registrert Fricamp-plass: en faktisk Fricamp-spot som er opprettet/verifisert
// av en bruker (ikke auto-importert fra OSM/NVDB). Dette er den ekte "nyhetsverdien".
const isUserContributedFricamp = (s: CampingSpot): boolean =>
  isFricampSpot(s) && (s.sourceLevel === 4 || !!s.createdBy || s.isVerified === true);

// Jon Borgen Takle (admin) har to bruker-dokumenter med samme e-post
// (jonborgentakle@gmail.com). Registreringer herfra er ofte test-/bulk-spots
// uten bilder og har lav nyhetsverdi.
const JONBORGEN_UIDS = new Set([
  'ry5H6FJ0pgh19PZ4bbK4tQgSRew1',
  '3SC7LNUCQBSXJsOvBQIginz8fe12',
]);

// Har plassen et bilde? Alle bilder teller – bruker-opplastede og auto-hentede.
const spotHasAnyPhoto = (s: CampingSpot): boolean =>
  !!((s.photoUrls && s.photoUrls.length > 0) || s.photoUrl || s.ugcThumbnailUrl || s.auto_image_url);

// Nedprioriter Jon Borgen Takle sine registreringer i news-feeden med mindre de
// har bilder. Disse skyves nederst (ikke fjernes), så de vises kun hvis det
// ikke er nok annet innhold.
const isDeprioritizedNewsSpot = (s: CampingSpot): boolean =>
  !!s.createdBy && JONBORGEN_UIDS.has(s.createdBy) && !spotHasAnyPhoto(s);

export const fetchRecentNewsSpots = async (
  limitCount = 10,
  enabledCountries?: Partial<Record<string, boolean>>
): Promise<CampingSpot[]> => {
  const byId = new Map<string, CampingSpot>();

  const addSpots = (spots: CampingSpot[], sourceName: string) => {
    firebaseDebugLog(`[NEWS] Adding ${spots.length} spots from ${sourceName}`);
    let eligibleCount = 0;

    for (const spot of spots) {
      if (spot.isDeleted || spot.isInvalid) continue;
      if (!isSpotInEnabledCountry(spot, enabledCountries)) continue;

      eligibleCount++;
      const prev = byId.get(spot.id);
      if (!prev || getSpotNewsTimestamp(spot) > getSpotNewsTimestamp(prev)) {
        byId.set(spot.id, spot);
      }
    }
    firebaseDebugLog(`[NEWS] Processed ${sourceName}. Eligible: ${eligibleCount}`);
  };

  // 1. Newest public spots
  try {
    const q = query(
      collection(db, "spots"),
      orderBy("createdAt", "desc"),
      limit(Math.max(limitCount * 8, 80))
    );
    const snap = await getDocs(q);
    addSpots(snap.docs.map(d => ({ ...(d.data() as CampingSpot), id: d.id })), 'spots.createdAt');
  } catch (e) {
    console.warn("[NEWS] spots.createdAt fetch failed", e);
  }

  // 2. Newest verified spots
  try {
    const q = query(
      collection(db, "spots"),
      where("isVerified", "==", true),
      orderBy("verifiedAt", "desc"),
      limit(Math.max(limitCount * 4, 40))
    );
    const snap = await getDocs(q);
    addSpots(snap.docs.map(d => ({ ...(d.data() as CampingSpot), id: d.id })), 'spots.verifiedAt');
  } catch (e) {
    firebaseDebugLog("[NEWS] verifiedAt query failed or no index, trying fallback fetchRecentVerifiedSpots...");
    try {
      const verifiedFallback = await fetchRecentVerifiedSpots(Math.max(limitCount * 4, 40));
      addSpots(verifiedFallback, 'fetchRecentVerifiedSpots');
    } catch (ve) {
      console.warn("[NEWS] fetchRecentVerifiedSpots failed", ve);
    }
  }

  // 3. Fallback to cached_spots if we don't have enough
  if (byId.size < limitCount) {
    try {
      firebaseDebugLog("[NEWS] Need more spots, trying cached_spots.createdAt...");
      const q = query(
        collection(db, "cached_spots"),
        orderBy("createdAt", "desc"),
        limit(Math.max(limitCount * 8, 80))
      );
      const snap = await getDocs(q);
      addSpots(snap.docs.map(d => ({ ...(d.data() as CampingSpot), id: d.id })), 'cached_spots.createdAt');
    } catch (e) {
      console.warn("[NEWS] cached_spots.createdAt fetch failed", e);
    }
  }

  if (byId.size < limitCount) {
    try {
      firebaseDebugLog("[NEWS] Final attempt: cached_spots.lastSyncedAt...");
      const q = query(
        collection(db, "cached_spots"),
        orderBy("lastSyncedAt", "desc"),
        limit(Math.max(limitCount * 12, 120))
      );
      const snap = await getDocs(q);
      addSpots(snap.docs.map(d => ({ ...(d.data() as CampingSpot), id: d.id })), 'cached_spots.lastSyncedAt');
    } catch (e) {
      console.warn("[NEWS] cached_spots.lastSyncedAt fetch failed", e);
    }
  }

  firebaseDebugLog(`[NEWS] Returning ${byId.size} spots total.`);
  // 80 % bruker-registrerte Fricamp-plasser (ekte nyhetsverdi), 20 % andre nye spots
  // (kommersielle campingplasser, OSM-amenities, etc.) slik at feed-en holdes levende.
  const all = Array.from(byId.values()).sort((a, b) => {
    // Nedprioriterte spots (Jon Borgen Takle uten bilder) skyves alltid bakerst,
    // uavhengig av tidsstempel.
    const da = isDeprioritizedNewsSpot(a) ? 1 : 0;
    const dbScore = isDeprioritizedNewsSpot(b) ? 1 : 0;
    if (da !== dbScore) return da - dbScore;
    return getSpotNewsTimestamp(b) - getSpotNewsTimestamp(a);
  });
  // Reserver ~30 % av feeden til bekreftet-/innsjekk-innhold. Uten en fast kvote
  // vinner "nye" spots alltid (det finnes alltid 80+ nyopprettede/importerte), og
  // bekreftet-tieren under ville aldri vises. Kvoten gis tilbake til nye spots
  // hvis det ikke finnes nok bekreftet innhold (se backfill nederst).
  const confirmedQuota = Math.floor(limitCount * 0.3);
  const newTierTarget = Math.max(1, limitCount - confirmedQuota);
  const userFricamp = all.filter(isUserContributedFricamp);
  const other = all.filter((s) => !isUserContributedFricamp(s));
  const userFricampQuota = Math.ceil(newTierTarget * 0.8);
  const userFricampPicks = userFricamp.slice(0, userFricampQuota);
  const otherPicks = other.slice(0, newTierTarget - userFricampPicks.length);
  const picked = [...userFricampPicks, ...otherPicks];
  if (picked.length < newTierTarget) {
    const seen = new Set(picked.map((s) => s.id));
    for (const s of all) {
      if (picked.length >= newTierTarget) break;
      if (!seen.has(s.id)) picked.push(s);
    }
  }
  // Alt over er genuint "nytt" innhold (rangert på createdAt/verifiedAt).
  const result: CampingSpot[] = picked.map((s) => ({ ...s, newsKind: 'new' as const }));

  // Bekreftet-tier: fyller de reserverte plassene (og evt. resten hvis det er tynt
  // med ekte nytt). To kilder slås sammen: nylig re-bekreftede plasser
  // (lastConfirmedAt) OG plasser med innsjekkinger (checkInCount). Plasser med bilder
  // prioriteres; de uten bilde vises med satellitt-tile + markør (se NewsSpotItem).
  // Alt merkes "confirmed" så vi aldri utgir gamle plasser for å være nye.
  if (result.length < limitCount) {
    try {
      const seen = new Set(result.map((s) => s.id));
      const candidates = new Map<string, CampingSpot>();

      const collect = (docs: CampingSpot[]) => {
        for (const s of docs) {
          if (s.isDeleted || s.isInvalid) continue;
          if (seen.has(s.id) || candidates.has(s.id)) continue;
          if (!isSpotInEnabledCountry(s, enabledCountries)) continue;
          candidates.set(s.id, s);
        }
      };

      const fillLimit = Math.max(limitCount * 2, 20);
      const [confirmedSnap, checkinSnap] = await Promise.allSettled([
        getDocs(query(collection(db, "cached_spots"), orderBy("lastConfirmedAt", "desc"), limit(fillLimit))),
        getDocs(query(collection(db, "cached_spots"), where("checkInCount", ">", 0), orderBy("checkInCount", "desc"), limit(fillLimit))),
      ]);
      if (confirmedSnap.status === 'fulfilled') {
        collect(confirmedSnap.value.docs.map((d) => ({ ...(d.data() as CampingSpot), id: d.id })));
      }
      if (checkinSnap.status === 'fulfilled') {
        collect(checkinSnap.value.docs.map((d) => ({ ...(d.data() as CampingSpot), id: d.id })));
      } else {
        console.warn("[NEWS] checkInCount fill failed", checkinSnap.reason);
      }

      // Recency for ranking: most recent check-in, else last confirmation.
      const confirmedRecency = (s: CampingSpot): number =>
        Math.max(Number((s as any).lastCheckInAt) || 0, Number((s as any).lastConfirmedAt) || 0);

      const ordered = Array.from(candidates.values()).sort((a, b) => {
        // 1) Plasser med bilde først.
        const pa = spotHasAnyPhoto(a) ? 0 : 1;
        const pb = spotHasAnyPhoto(b) ? 0 : 1;
        if (pa !== pb) return pa - pb;
        // 2) Deretter nyeste aktivitet.
        return confirmedRecency(b) - confirmedRecency(a);
      });

      for (const s of ordered) {
        if (result.length >= limitCount) break;
        result.push({ ...s, newsKind: 'confirmed' as const });
      }
    } catch (e) {
      console.warn("[NEWS] confirmed-tier fill failed", e);
    }
  }
  // Backfill: fantes det ikke nok bekreftet innhold til kvoten, gis de reserverte
  // plassene tilbake til nye spots så feeden alltid er full.
  if (result.length < limitCount) {
    const seen = new Set(result.map((s) => s.id));
    for (const s of all) {
      if (result.length >= limitCount) break;
      if (!seen.has(s.id)) {
        result.push({ ...s, newsKind: 'new' as const });
        seen.add(s.id);
      }
    }
  }
  return result;
};

export interface RecentReview {
  id: string;
  spotId: string;
  spotName?: string;
  spotLocation?: CampingSpot['location'];
  spotPhotoUrl?: string;
  spotPhotoUrls?: string[];
  userId: string;
  userName: string;
  userPhoto?: string;
  userTier?: number;
  rating: number;
  comment: string;
  photoUrl?: string;
  photoUrls?: string[];
  createdAt: any;
}

const fetchSpotForReview = async (spotId: string): Promise<CampingSpot | null> => {
  if (!spotId) return null;
  const collections = ['cached_spots', 'spots'];
  for (const collectionName of collections) {
    try {
      const snap = await getDoc(doc(db, collectionName, spotId));
      if (snap.exists()) return { ...(snap.data() as CampingSpot), id: snap.id };
    } catch {
      // Try the next public spot collection.
    }
  }
  return null;
};

export const fetchRecentReviews = async (limitCount = 10): Promise<RecentReview[]> => {
  try {
    const q = query(
      collection(db, 'reviews'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    const reviews = snap.docs.map(d => ({ ...(d.data() as RecentReview), id: d.id }));
    return Promise.all(reviews.map(async (review) => {
      const spot = await fetchSpotForReview(review.spotId);
      const reviewPhotoUrls = Array.isArray(review.photoUrls) ? review.photoUrls.filter(Boolean) : [];
      const spotPhotoUrls = Array.isArray(spot?.photoUrls) ? spot.photoUrls.filter(Boolean) : [];
      return {
        ...review,
        photoUrl: review.photoUrl || reviewPhotoUrls[0] || '',
        spotName: review.spotName || spot?.name || '',
        spotLocation: spot?.location,
        spotPhotoUrl: spot?.photoUrl || spotPhotoUrls[0] || spot?.auto_image_url || '',
        spotPhotoUrls,
      };
    }));
  } catch {
    try {
      const q2 = query(collection(db, 'reviews'), limit(limitCount));
      const snap2 = await getDocs(q2);
      const reviews = snap2.docs.map(d => ({ ...(d.data() as RecentReview), id: d.id }));
      return Promise.all(reviews.map(async (review) => {
        const spot = await fetchSpotForReview(review.spotId);
        const reviewPhotoUrls = Array.isArray(review.photoUrls) ? review.photoUrls.filter(Boolean) : [];
        const spotPhotoUrls = Array.isArray(spot?.photoUrls) ? spot.photoUrls.filter(Boolean) : [];
        return {
          ...review,
          photoUrl: review.photoUrl || reviewPhotoUrls[0] || '',
          spotName: review.spotName || spot?.name || '',
          spotLocation: spot?.location,
          spotPhotoUrl: spot?.photoUrl || spotPhotoUrls[0] || spot?.auto_image_url || '',
          spotPhotoUrls,
        };
      }));
    } catch {
      return [];
    }
  }
};

// Har brukeren allerede anmeldt denne plassen? Brukes til å unngå å mase om
// anmeldelse etter innsjekk når det allerede finnes en.
export const hasUserReviewedSpot = async (spotId: string, userId: string): Promise<boolean> => {
  if (!spotId || !userId) return false;
  try {
    const q = query(
      collection(db, 'reviews'),
      where('spotId', '==', spotId),
      where('userId', '==', userId),
      limit(1)
    );
    const snap = await getDocs(q);
    return !snap.empty;
  } catch {
    return false;
  }
};

// Private spots are stored in /users/{uid}/private_spots/{spotId} — owner-only.
export const upsertPrivateSpot = async (uid: string, spot: CampingSpot) => {
  if (!uid || !spot?.id) return;
  const normalized: CampingSpot = {
    ...spot,
    visibility: 'private',
    createdBy: spot.createdBy || uid,
    lastSyncedAt: spot.lastSyncedAt || new Date().toISOString(),
  };
  const ref = doc(db, 'users', uid, 'private_spots', spot.id);
  await setDoc(ref, cleanFirestoreData(normalized), { merge: true });
};

export const deletePrivateSpot = async (uid: string, spotId: string) => {
  if (!uid || !spotId) return;
  const ref = doc(db, 'users', uid, 'private_spots', spotId);
  await deleteDoc(ref);
};

export const fetchPrivateSpots = async (uid: string): Promise<CampingSpot[]> => {
  if (!uid) return [];
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'private_spots'));
    return snap.docs.map(d => ({ ...(d.data() as CampingSpot), id: d.id, visibility: 'private' as const }));
  } catch (err) {
    console.warn('[fetchPrivateSpots] failed:', err);
    return [];
  }
};

// --- Client-side admin gate for shared-spot curation ----------------------
// Mirrors `isAdminUser(currentUser)` (set from App.tsx whenever the user
// changes). Non-admins are forbidden by firestore.rules from touching
// isInvalid/invalidReason on cached_spots or from writing other users' docs
// in `spots`. Attempting those writes used to surface as unhandled
// `permission-denied` rejections (the #1 error_report). We consult this flag
// to skip the doomed writes instead of attempting and failing.
let _clientIsAdmin = false;
export const setClientAdminStatus = (isAdmin: boolean): void => {
  _clientIsAdmin = !!isAdmin;
};

export const upsertSpotsToFirebase = async (spots: CampingSpot[]) => {
  // Synthetic waypoint/visit spots (id prefixed with `wpcard-` or `visit_`)
  // are ephemeral renderings of `user_content` / `user_visits` and must
  // NEVER be persisted to public collections. Persisting them creates
  // runaway cascades like `wpcard-visit_wpcard-visit_...manual-xyz`
  // because each persisted doc gets re-synthesized on the next read,
  // pollutes the news feed, and can't be cleanly admin-deleted.
  const synthetic = spots.filter(s => isSyntheticSpotId(s?.id));
  if (synthetic.length > 0 && import.meta.env?.DEV) {
    console.warn(`[upsertSpotsToFirebase] skipped ${synthetic.length} synthetic id(s)`, synthetic.map(s => s.id));
  }
  spots = spots.filter(s => typeof s.id === 'string' && !isSyntheticSpotId(s.id));
  if (spots.length === 0) return;
  const batch = writeBatch(db);
  const resolveCanonicalDocId = async (collectionName: "cached_spots" | "spots", spot: CampingSpot): Promise<string> => {
    if (!spot.externalId) return spot.id;
    const inferredSource = getSpotSource(spot);
    if (!inferredSource) return spot.id;
    try {
      const q = query(
        collection(db, collectionName),
        where("source", "==", inferredSource),
        where("externalId", "==", spot.externalId),
        limit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].id;
    } catch {
      // Ignore query/index issues and fallback to incoming id.
    }
    return spot.id;
  };

  const uid = auth.currentUser?.uid;
  let queuedWrites = 0;

  for (const spot of spots) {
    const normalizedSource = getSpotSource(spot);
    // GEOCELLER SKRIVES HER, IKKE BARE AV TRIGGEREN.
    //
    // `getSpotMarkers` har et OBLIGATORISK `where(field, 'in', cells)`-filter paa
    // geocelle-feltet. Firestore utelater dokumenter som MANGLER feltet - stille. En
    // plass uten `geocell` er derfor usynlig paa kartet for alltid.
    //
    // Serveren har en vakt for dette (`onSpotEnsureGeoFields`, onCreate paa
    // cached_spots), men den er avhengig av at det FOERSTE skrivet inneholder
    // koordinaten. Det holdt ikke: `uploadSpotImages` -> `updateSpotPhotoUrls`
    // kjoerer FOER plassen lagres, og siden 6a7f9e3 (16.08.2026) oppretter den
    // dokumentet med `setDoc(..., {merge:true})` naar det ikke finnes - med bare
    // `photoUrls` og `updatedAt`, uten `location`. Triggeren fyrte da paa et
    // dokument uten koordinat, returnerte tomt etter ~50 ms, og det ekte lagringen
    // etterpaa var en UPDATE som onCreate aldri ser.
    //
    // Rapportert 23.08.2026: tre plasser lagt inn i Osa i Hardanger 22.-23.08 laa i
    // Firestore med riktige koordinater, men dukket ikke opp i kartet.
    //
    // Ved aa regne ut cellene her er synligheten ikke lenger avhengig av
    // skriverekkefoelge, kaldstart eller trigger-forsinkelse. Triggeren blir staaende
    // som nett for server-/script-skrivere.
    const cells = spot.location
      && typeof spot.location.latitude === 'number'
      && typeof spot.location.longitude === 'number'
      ? geocellFields(spot.location.latitude, spot.location.longitude)
      : null;

    const normalizedSpot: CampingSpot = {
      ...spot,
      ...(cells || {}),
      source: normalizedSource || spot.source,
      lastSyncedAt: spot.lastSyncedAt || new Date().toISOString(),
      qualityTier: computeQualityTier(spot),
    };

    // A non-admin may only write the `spots` mirror for a spot they own
    // (createdBy == uid && sourceLevel == 4), matching isOwnerCreatedSpotWrite().
    const isOwnSpot = !!uid && normalizedSpot.createdBy === uid && normalizedSpot.sourceLevel === 4;

    // Basic cached spot
    const cachedDocId = await resolveCanonicalDocId("cached_spots", normalizedSpot);
    const ref = doc(db, "cached_spots", cachedDocId);
    const cleanSpot = cleanFirestoreData({ ...normalizedSpot, id: cachedDocId });
    if (!_clientIsAdmin) {
      // firestore.rules: non-admins may not set/change isInvalid or invalidReason
      // on cached_spots. Stripping them keeps the (allowed) field updates while
      // avoiding a permission-denied that would fail the whole batch.
      delete (cleanSpot as any).isInvalid;
      delete (cleanSpot as any).invalidReason;
    }
    batch.set(ref, cleanSpot, { merge: true });
    queuedWrites++;

    // If it's a confirmed/user-contributed spot, also update the main spots collection.
    // Only admins (or the spot's owner) are permitted to write here — skip for everyone
    // else so the batch doesn't fail on a forbidden mirror write.
    if (normalizedSpot.sourceLevel === 4 || normalizedSpot.spotType === 'confirmed' || normalizedSpot.createdBy) {
      if (_clientIsAdmin || isOwnSpot) {
        const mainDocId = await resolveCanonicalDocId("spots", normalizedSpot);
        const mainRef = doc(db, "spots", mainDocId);
        const cleanMainSpot = cleanFirestoreData({ ...normalizedSpot, id: mainDocId });
        batch.set(mainRef, cleanMainSpot, { merge: true });
        queuedWrites++;
      }
    }
  }
  if (queuedWrites === 0) return;
  await batch.commit();
};

export const uploadSpotImages = async (spotId: string, userId: string, files: File[]): Promise<string[]> => {
  if (!spotId || !userId || files.length === 0) return [];
  const urls: string[] = [];
  const userName = auth.currentUser?.displayName || i18n.t('firebase.anonymous', 'Anonym');
  for (const original of files) {
    // Komprimer HER, ikke bare hos kallerne. Alt som ble lastet opp før
    // komprimeringen kom inn i kallstedene (0a940cf) ligger i Storage på 8–15 MB;
    // med garantien i selve opplastingen kan en ny kallsti ikke gjenta feilen.
    // Allerede små bilder slippes gjennom urørt, så vi ikke komprimerer to ganger.
    let file: File;
    try {
      file = await ensureCompressedImage(original);
    } catch (e) {
      // Format nettleseren ikke kan tegne (kamera-RAW). Kallstedene validerer og
      // varsler brukeren; her hopper vi bare over filen framfor å laste opp noe
      // som garantert blir et brutt bilde.
      console.warn('[uploadSpotImages] hopper over fil som ikke kan vises:', original.name, e);
      continue;
    }
    const safeName = file.name.replace(/\s+/g, '_');
    const storagePath = `spots/${spotId}/${userId}_${Date.now()}_${safeName}`;
    const fileRef = ref(storage, storagePath);
    // Sett contentType eksplisitt — komprimerte bilder kan miste MIME-type
    const metadata = { contentType: file.type || 'image/jpeg' };
    const snap = await uploadBytes(fileRef, file, metadata);
    const url = await getDownloadURL(snap.ref);
    urls.push(url);
    // Miniatyr for listevisningene (getThumbUrl). Uten den laster hvert kort
    // originalen i full størrelse inn i en 36×36-rute.
    await uploadThumbFor(storagePath, file);

    // Opprett doc i images-underkolleksjonen så live-abonnementer (SpotCard/MobileSpotSheet) fanger opp bildet
    try {
      const imageDoc: Omit<SpotImage, 'id'> = {
        image_url: url,
        uploaded_by: userId,
        uploaded_by_name: userName,
        uploaded_at: Date.now(),
        source: 'user',
      };
      const colRef = collection(db, 'cached_spots', spotId, 'images');
      await setDoc(doc(colRef), cleanFirestoreData(imageDoc));
    } catch (e) {
      console.warn('[uploadSpotImages] Kunne ikke lagre i images-underkolleksjon:', e);
    }
  }

  if (urls.length > 0) {
    try {
      await updateSpotPhotoUrls(spotId, urls);
    } catch (e) {
      console.warn('[uploadSpotImages] Kunne ikke oppdatere photoUrls på spot:', e);
    }
  }

  return urls;
};

// ── Spot Image UGC (subcollection) ───────────────────────────────────────

export interface SpotImage {
  id: string;
  image_url: string;
  uploaded_by: string;
  uploaded_by_name: string;
  uploaded_at: number;
  source?: 'user' | 'google' | 'wikimedia' | 'mapillary';
  cachedAt?: number;
  googlePhotoRef?: string;
}

export const uploadSpotImage = async (
  spotId: string,
  userId: string,
  userName: string,
  file: File
): Promise<SpotImage> => {
  // ensureCompressedImage, ikke compressImage: SpotImageUpload komprimerer allerede
  // før den kaller hit, og en ny runde koster bare kvalitet. Garantien blir den
  // samme for kallsteder som ikke har komprimert.
  const compressed = await ensureCompressedImage(file);
  const safeName = compressed.name.replace(/\s+/g, '_');
  const storagePath = `spots/${spotId}/user_images/${userId}_${Date.now()}_${safeName}`;
  const fileRef = ref(storage, storagePath);
  // Sett contentType eksplisitt — komprimerte bilder kan miste MIME-type, og
  // uten image/* defaulter Storage til octet-stream → permission-denied på regelen.
  const metadata = { contentType: compressed.type || 'image/jpeg' };
  const snap = await uploadBytes(fileRef, compressed, metadata);
  const imageUrl = await getDownloadURL(snap.ref);
  await uploadThumbFor(storagePath, compressed);

  const imageDoc: Omit<SpotImage, 'id'> = {
    image_url: imageUrl,
    uploaded_by: userId,
    uploaded_by_name: userName,
    uploaded_at: Date.now(),
  };

  const colRef = collection(db, 'cached_spots', spotId, 'images');
  const docRef = doc(colRef);
  await setDoc(docRef, cleanFirestoreData(imageDoc));

  // Set first UGC image as thumbnail on the spot document (for mini cards etc.)
  const spotRef = doc(db, 'cached_spots', spotId);
  const spotSnap = await getDoc(spotRef);
  if (spotSnap.exists() && !spotSnap.data().ugcThumbnailUrl) {
    await setDoc(spotRef, { ugcThumbnailUrl: imageUrl }, { merge: true });
  }

  return { id: docRef.id, ...imageDoc };
};

export const subscribeToSpotImages = (
  spotId: string,
  callback: (images: SpotImage[]) => void
) => {
  const q = query(
    collection(db, 'cached_spots', spotId, 'images'),
    orderBy('uploaded_at', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const images = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as SpotImage));
    callback(sortSpotImagesUserFirst(images));
  }, () => callback([]));
};

// Engangs-henting av en plass' bilder (bruker-bilder først, så Google/auto).
// Brukt av feed-kort-karusellen som laster resten av bildene FØRST når brukeren
// forsøker å bla — ikke ved lasting av lista. Returnerer image_url-liste.
export const getSpotImages = async (spotId: string): Promise<string[]> => {
  if (!spotId) return [];
  try {
    const q = query(
      collection(db, 'cached_spots', spotId, 'images'),
      orderBy('uploaded_at', 'desc'),
    );
    const snap = await getDocs(q);
    const images = snap.docs.map(d => ({ id: d.id, ...d.data() } as SpotImage));
    return sortSpotImagesUserFirst(images).map(i => i.image_url).filter(Boolean);
  } catch {
    return [];
  }
};

export const fetchGooglePhotosForSpot = async (spotId: string): Promise<void> => {
  const callable = httpsCallable(functions, 'fetchGooglePlacePhotos');
  await callable({ spotId });
};

// Delt bilde-resolver for ALLE kartlag: stedsnøyaktig bilde for et vilkårlig
// koordinat-punkt. mapillary/street view. url er direkte (mapillary) eller en
// signert live-proxy (street view). source==='none' → intet bilde tilgjengelig.
export interface ResolvedLocationImage {
  source: 'mapillary' | 'streetview' | 'none' | string;
  url: string | null;
  credit: string | null;
  date: string | null;
}
export const resolveLocationImage = async (lat: number, lng: number): Promise<ResolvedLocationImage> => {
  const callable = httpsCallable(functions, 'resolveLocationImage');
  const res = await callable({ lat, lng });
  return (res.data || { source: 'none', url: null, credit: null, date: null }) as ResolvedLocationImage;
};

export interface GoogleReview {
  author: string;
  authorPhoto: string;
  authorUri: string;
  rating: number | null;
  text: string;
  relativeTime: string;
  publishTime: string;
}

export interface GoogleAiSuitability {
  suitable: boolean;
  confidence: number;
  scoreBoost: number;
  summary: string;
  checkedAt: number;
}

export interface GoogleReviewsResult {
  status: string;
  reviews: GoogleReview[];
  rating: number | null;
  userRatingCount: number | null;
  googleMapsUri: string | null;
  // AI-vurdert fricamp-egnethet (Google som skjult input; aldri kreditert i UI).
  aiSuitability: GoogleAiSuitability | null;
}

// Henter Google-anmeldelser LIVE. Anmeldelsestekst lagres aldri (Googles vilkår) —
// kalleren holder resultatet i minne og viser det direkte med attribusjon.
export const fetchGoogleReviewsForSpot = async (spotId: string): Promise<GoogleReviewsResult> => {
  const callable = httpsCallable(functions, 'fetchGooglePlaceReviews');
  const res = await callable({ spotId });
  const data = (res.data || {}) as Partial<GoogleReviewsResult>;
  return {
    status: data.status || 'error',
    reviews: Array.isArray(data.reviews) ? data.reviews : [],
    rating: data.rating ?? null,
    userRatingCount: data.userRatingCount ?? null,
    googleMapsUri: data.googleMapsUri ?? null,
    aiSuitability: (data.aiSuitability as GoogleAiSuitability | undefined) ?? null,
  };
};

export const markSpotAsInvalid = async (spotId: string, reason: string) => {
  // Invalidating a shared spot writes isInvalid/invalidReason, which
  // firestore.rules restricts to admins. Non-admins (e.g. the client-side
  // auto-clean / re-analysis flows) must not attempt it — otherwise the write
  // rejects with permission-denied and, on fire-and-forget paths, surfaces as
  // an unhandled rejection. Local UI state removal is handled by the caller.
  if (!_clientIsAdmin) {
    console.warn('[markSpotAsInvalid] Skipped — only admins may invalidate shared spots', { spotId, reason });
    return;
  }
  console.log('[ADMIN-DELETE] markSpotAsInvalid writing to Firestore', { spotId, reason });
  const payload = { isInvalid: true, invalidReason: reason };
  // cached_spots gets created-or-merged. The `spots` mirror is updateDoc-only
  // — fails with `not-found` when the doc doesn't exist, which we silently
  // swallow. This avoids creating empty `spots` docs for cached-only entries
  // (algorithmic, OSM-derived) while still mirroring for confirmed/user-
  // contributed spots that the news feed reads from.
  await Promise.all([
    setDoc(doc(db, "cached_spots", spotId), payload, { merge: true }),
    updateDoc(doc(db, "spots", spotId), payload).catch((e: any) => {
      if (e?.code !== 'not-found') {
        console.warn('[ADMIN-DELETE] Failed to mirror invalidation to spots', e);
      }
    }),
  ]);
  console.log('[ADMIN-DELETE] Firestore write completed for', spotId);
};

export const deleteAlgorithmicSpotsInBounds = async (bounds: MapBounds) => {
  const spots = await getCachedSpotsInArea(bounds);
  const algoSpots = spots.filter(s => s.spotType === 'algorithmic' && s.sourceLevel !== 4);

  if (algoSpots.length === 0) return;

  const batch = writeBatch(db);
  algoSpots.forEach(s => {
    const ref = doc(db, "cached_spots", s.id);
    batch.delete(ref);
  });
  await batch.commit();
};

export const banSpotGlobally = async (spotId: string, reason: string) => {
  console.log('[ADMIN-DELETE] banSpotGlobally called', { spotId, reason });
  await markSpotAsInvalid(spotId, reason);
  console.log('[ADMIN-DELETE] banSpotGlobally done for', spotId);
};

/**
 * Finn duplikat-spots opprettet av en bruker (typisk fra cascade-bug eller
 * gjentatt manuell test-add). Grupperer på (rundet lat/lon + lowercased name)
 * og beholder eldste i hver gruppe — markerer resten som isInvalid.
 *
 * dryRun=true returnerer kun analysen uten å skrive noe.
 */
export interface DedupeResult {
  totalScanned: number;
  groups: Array<{
    key: string;
    name: string;
    keepId: string;
    keepCreatedAt: number;
    duplicateIds: string[];
  }>;
  totalToInvalidate: number;
  applied: boolean;
}

/**
 * «Din påvirkning» (#4B Endowment): les brukerens egne plasser klientside og
 * returner minimal projeksjon for impact-beregning (utils/userImpact.ts).
 * Read-only, gyldige (ikke-invaliderte) plasser.
 */
export const fetchUserCreatedSpotsImpact = async (
  userId: string,
): Promise<Array<{ visitCount?: number; isVerified?: boolean; county?: string }>> => {
  if (!userId) return [];
  const spotsRef = collection(db, 'cached_spots');
  const snap = await getDocs(query(spotsRef, where('createdBy', '==', userId)));
  const out: Array<{ visitCount?: number; isVerified?: boolean; county?: string }> = [];
  snap.forEach(d => {
    const data: any = d.data();
    if (data?.isInvalid) return;
    out.push({
      visitCount: Number(data?.visitCount) || 0,
      isVerified: !!data?.isVerified,
      county: typeof data?.county === 'string' ? data.county : undefined,
    });
  });
  return out;
};

export const dedupeUserCreatedSpots = async (
  userId: string,
  options: { dryRun: boolean; reason?: string } = { dryRun: true }
): Promise<DedupeResult> => {
  const reason = options.reason || 'Auto-dedupe: duplikat opprettet av samme bruker på samme lokasjon';
  const spotsRef = collection(db, 'cached_spots');
  const q = query(spotsRef, where('createdBy', '==', userId));
  const snap = await getDocs(q);

  const round = (n: number) => Math.round(n * 10000) / 10000; // ~11m presisjon
  const groups = new Map<string, Array<{ id: string; name: string; createdAt: number; isInvalid: boolean }>>();

  let totalScanned = 0;
  snap.forEach(d => {
    const data: any = d.data();
    if (!data?.location) return;
    if (data.isInvalid) return; // allerede invalidert — hopp over
    totalScanned++;
    const key = `${round(data.location.latitude)}_${round(data.location.longitude)}_${(data.name || '').toLowerCase().trim()}`;
    const list = groups.get(key) || [];
    list.push({
      id: d.id,
      name: data.name || '',
      createdAt: Number(data.createdAt) || 0,
      isInvalid: !!data.isInvalid,
    });
    groups.set(key, list);
  });

  const dupGroups: DedupeResult['groups'] = [];
  let totalToInvalidate = 0;
  for (const [key, items] of groups.entries()) {
    if (items.length < 2) continue;
    items.sort((a, b) => a.createdAt - b.createdAt);
    const keep = items[0];
    const dups = items.slice(1);
    dupGroups.push({
      key,
      name: keep.name,
      keepId: keep.id,
      keepCreatedAt: keep.createdAt,
      duplicateIds: dups.map(d => d.id),
    });
    totalToInvalidate += dups.length;
  }

  if (!options.dryRun && totalToInvalidate > 0) {
    const batch = writeBatch(db);
    for (const g of dupGroups) {
      for (const dupId of g.duplicateIds) {
        const ref = doc(db, 'cached_spots', dupId);
        batch.set(ref, { isInvalid: true, invalidReason: reason }, { merge: true });
      }
    }
    await batch.commit();
  }

  return {
    totalScanned,
    groups: dupGroups,
    totalToInvalidate,
    applied: !options.dryRun,
  };
};

// Subscriptions
export const subscribeToFavorites = (userId: string, callback: (ids: string[]) => void) => {
  return onSnapshot(doc(db, "user_favorites", userId), (snapshot) => {
    callback(snapshot.exists() ? snapshot.data().spotIds || [] : []);
  }, () => callback([]));
};

export const toggleFavoriteSpot = async (spotId: string, userId: string, currentStatus: boolean) => {
  const ref = doc(db, "user_favorites", userId);
  if (currentStatus) {
    await setDoc(ref, { spotIds: arrayRemove(spotId) }, { merge: true });
  } else {
    await setDoc(ref, { spotIds: arrayUnion(spotId) }, { merge: true });
  }
};

export const subscribeToHiddenSpots = (userId: string, callback: (ids: string[]) => void) => {
  return onSnapshot(doc(db, "user_hidden_spots", userId), (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data().spotIds || []);
    } else {
      callback([]);
    }
  }, () => callback([]));
};

export const subscribeToFavoriteRoutes = (userId: string, callback: (ids: string[]) => void) => {
  return onSnapshot(doc(db, "user_favorite_routes", userId), (doc) => {
    callback(doc.exists() ? doc.data().routeIds || [] : []);
  }, () => callback([]));
};

export const toggleFavoriteRoute = async (routeId: string, userId: string, currentStatus: boolean) => {
  const ref = doc(db, "user_favorite_routes", userId);
  if (currentStatus) {
    await setDoc(ref, { routeIds: arrayRemove(routeId) }, { merge: true });
  } else {
    await setDoc(ref, { routeIds: arrayUnion(routeId) }, { merge: true });
  }
};

export const fetchAllRoutes = async (): Promise<GpxRoute[]> => {
  const q = query(collection(db, "routes"), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GpxRoute));
};

export const fetchPendingRoutes = async (): Promise<GpxRoute[]> => {
  const q = query(collection(db, "routes"), where("status", "==", "pending"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GpxRoute));
};

const PROXY_URL = "https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy";

export const fetchGpxWithProxy = async (url: string): Promise<string> => {
  const needsProxy = url.includes('firebasestorage.googleapis.com') || url.includes('storage.googleapis.com');

  if (!needsProxy) {
    try {
      const response = await fetch(url);
      if (response.ok) return await response.text();
    } catch (_) {
      // Direct fetch failed, fall through to proxy
    }
  }

  const proxyUrl = `${PROXY_URL}?url=${encodeURIComponent(url)}`;
  const proxyResponse = await fetch(proxyUrl);
  if (!proxyResponse.ok) throw new Error(`GPX fetch failed (${proxyResponse.status})`);
  return await proxyResponse.text();
};

const safeSlugify = (text?: string): string => {
  if (!text) return '';
  return text.toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const uploadGpxRoute = async (
  user: AppUser,
  name: string,
  description: string,
  difficulty: RouteDifficulty,
  file: File,
  status: RouteStatus,
  photos: File[],
  region: RouteRegion,
  estimatedTime: string,
  elevationProfile: ElevationPoint[],
  stats: RouteStats,
  externalLinks: RouteExternalLink[],
  distanceKm: number,
  wikiSpots?: WikiSpot[],
  startPlace?: string,
  endPlace?: string,
  routeWaypoints?: import('../types').RouteWaypoint[],
  startCoords?: import('../types').Coordinates,
  endCoords?: import('../types').Coordinates,
  suitability?: { suitableCategories?: import('../types').VehicleCategory[]; terrainTypes?: import('../types').TerrainPreference[]; requires4wd?: boolean; maxRecommendedLengthCm?: number; suitabilityNote?: string },
  seasonal?: { bestSeasons?: ('spring' | 'summer' | 'autumn' | 'winter')[]; winterSuitability?: 'good' | 'limited' | 'not_recommended'; seasonalNote?: string },
  sourceTrackId?: string
) => {
  // 1. Upload GPX file
  const storageRef = ref(storage, `routes/${user.uid}/${Date.now()}_${file.name}`);
  const snapshot = await uploadBytes(storageRef, file);
  const gpxUrl = await getDownloadURL(snapshot.ref);

  // 2. Upload Photos (compressed)
  const compressedPhotos = await compressImages(photos);
  const imageUrls: string[] = [];
  for (const photo of compressedPhotos) {
    const imgRef = ref(storage, `routes_images/${user.uid}/${Date.now()}_${photo.name}`);
    // contentType eksplisitt — uten image/* defaulter Storage til octet-stream → permission-denied.
    const imgSnap = await uploadBytes(imgRef, photo, { contentType: photo.type || 'image/jpeg' });
    const url = await getDownloadURL(imgSnap.ref);
    imageUrls.push(url);
  }

  // 3. Create Route Doc
  const slug = safeSlugify(name);
  const routeData: Partial<GpxRoute> = {
    name,
    slug,
    description,
    difficulty,
    gpxUrl,
    createdBy: user.uid,
    createdByName: user.displayName || i18n.t('firebase.anonymous'),
    createdByPhoto: user.photoURL || undefined,
    ...(user.activeVehicleCategory ? {
      creatorVehicleCategory: user.activeVehicleCategory,
      creatorVehicleLength: user.activeVehicleLength || undefined,
      creatorVehicleIs4wd: user.activeVehicleEquipment?.four_wheel_drive || undefined,
    } : {}),
    createdAt: serverTimestamp(),
    status,
    region,
    imageUrls,
    estimatedTime,
    elevationProfile,
    stats,
    externalLinks,
    distance: distanceKm,
    wikiSpots,
    ...(startPlace ? { startPlace } : {}),
    ...(endPlace ? { endPlace } : {}),
    ...(routeWaypoints && routeWaypoints.length > 0 ? { routeWaypoints } : {}),
    ...(startCoords ? { startCoords } : {}),
    ...(endCoords ? { endCoords } : {}),
    ...(suitability?.suitableCategories?.length ? {
      suitableCategories: suitability.suitableCategories,
      terrainTypes: suitability.terrainTypes || [],
      requires4wd: suitability.requires4wd || false,
      maxRecommendedLengthCm: suitability.maxRecommendedLengthCm,
      suitabilityNote: suitability.suitabilityNote,
    } : {}),
    ...(seasonal?.bestSeasons?.length ? { bestSeasons: seasonal.bestSeasons } : {}),
    ...(seasonal?.winterSuitability ? { winterSuitability: seasonal.winterSuitability } : {}),
    ...(seasonal?.seasonalNote ? { seasonalNote: seasonal.seasonalNote } : {}),
    ...(sourceTrackId ? { sourceTrackId } : {}),
  };

  const docRef = doc(collection(db, "routes"));
  const cleaned = cleanFirestoreData(routeData);
  // Guard: don't create route doc if essential fields were stripped
  if (!cleaned.name || !cleaned.gpxUrl || !cleaned.createdBy) {
    console.error('[uploadGpxRoute] Essential fields missing after clean, aborting. Keys:', Object.keys(cleaned));
    return docRef.id; // Return ID but don't persist broken doc
  }
  await setDoc(docRef, cleaned);
  return docRef.id;
};

export const updateGpxRoute = async (id: string, data: Partial<GpxRoute>) => {
  const ref = doc(db, "routes", id);
  const payload = { ...data };
  if (payload.name) {
    payload.slug = safeSlugify(payload.name);
  }
  try {
    await updateDoc(ref, cleanFirestoreData(payload));
  } catch (err: any) {
    const msg = String(err?.message || err || '');
    // Firestore SDK 12.9.0 internal assertion bug — fall back to Cloud Function
    if (msg.includes('INTERNAL ASSERTION') || msg.includes('Unexpected state')) {
      console.warn('[updateGpxRoute] Firestore SDK corrupted, using Cloud Function fallback');
      const callable = httpsCallable(functions, 'updateRouteDocument');
      const result = await callable({ routeId: id, data: cleanFirestoreData(payload) });
      const res = result.data as { ok?: boolean };
      if (!res?.ok) throw new Error('Cloud Function fallback also failed');
      return;
    }
    throw err;
  }
};

export const deleteRoute = async (id: string) => {
  await deleteDoc(doc(db, "routes", id));
};

export const recommendRouteForPublishing = async (route: GpxRoute, user: AppUser) => {
  await updateGpxRoute(route.id, { status: 'pending' as RouteStatus });
  const recRef = doc(collection(db, "route_recommendations"));
  await setDoc(recRef, cleanFirestoreData({
    routeId: route.id,
    routeName: route.name,
    userId: user.uid,
    userName: user.displayName || 'Anonym',
    createdAt: Date.now(),
    status: 'pending'
  }));
};

// Reviews
export const subscribeToReviews = (spotId: string, callback: (reviews: any[]) => void) => {
  const q = query(collection(db, "reviews"), where("spotId", "==", spotId), orderBy("createdAt", "desc"));
  let fallbackUnsub: (() => void) | null = null;
  const mainUnsub = onSnapshot(
    q,
    (snapshot) => {
      const reviews = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(reviews);
    },
    (error) => {
      if (error.code === 'failed-precondition') {
        console.warn("Missing Firestore Index for reviews. Using fallback query without orderBy.");
        if (!fallbackUnsub) {
          const fallbackQ = query(collection(db, "reviews"), where("spotId", "==", spotId));
          fallbackUnsub = onSnapshot(
            fallbackQ,
            (snapshot) => {
              const reviews = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a: any, b: any) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));
              callback(reviews);
            },
            (fallbackErr) => {
              console.error("Error in fallback review fetch:", fallbackErr);
            }
          );
        }
      } else {
        console.error("Error fetching reviews:", error);
      }
    }
  );
  return () => {
    mainUnsub();
    if (fallbackUnsub) fallbackUnsub();
  };
};

export const addReview = async (
  spotId: string,
  user: { uid: string; displayName: string | null; photoURL: string | null; activeVehicleCategory?: string; activeVehicleLength?: number; activeVehicleEquipment?: { four_wheel_drive?: boolean } },
  rating: number,
  comment: string,
  photos?: File[],
  videoUrl?: string,
  spotName?: string
) => {
  const photoUrls: string[] = [];
  if (photos && photos.length > 0) {
    const compressedPhotos = await compressImages(photos);
    for (const photo of compressedPhotos) {
      const imgRef = ref(storage, `reviews/${spotId}/${Date.now()}_${photo.name}`);
      // contentType eksplisitt — uten image/* defaulter Storage til octet-stream → permission-denied.
      const snap = await uploadBytes(imgRef, photo, { contentType: photo.type || 'image/jpeg' });
      photoUrls.push(await getDownloadURL(snap.ref));
    }
  }

  const normalizedVideoUrl = videoUrl?.trim() || '';

  const reviewData = {
    spotId,
    spotName: spotName || '',
    userId: user.uid,
    userName: user.displayName || i18n.t('firebase.anonymous'),
    userPhoto: user.photoURL,
    rating,
    comment,
    photoUrl: photoUrls[0] || '',
    photoUrls,
    videoUrl: normalizedVideoUrl,
    createdAt: Date.now(),
    ...(user.activeVehicleCategory ? {
      vehicleCategory: user.activeVehicleCategory,
      vehicleLength: user.activeVehicleLength || undefined,
      vehicleIs4wd: user.activeVehicleEquipment?.four_wheel_drive || undefined,
    } : {}),
  };

  const reviewRef = doc(collection(db, "reviews"));
  await setDoc(reviewRef, cleanFirestoreData(reviewData));
};

export const updateCampsiteStatus = async (
  spotId: string,
  fields: Partial<import('../types').CampingSpot['commercialMetadata'] & Record<string, unknown>>,
): Promise<void> => {
  const spotRef = doc(db, 'cached_spots', spotId);
  const update: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(fields)) {
    update[`commercialMetadata.${k}`] = v;
  }
  await updateDoc(spotRef, update);
};

export const addCampingRating = async (
  spotId: string,
  user: { uid: string; displayName: string | null; photoURL: string | null },
  campingRatings: import('../types').CampingRatings,
  comment: string,
  spotName?: string,
) => {
  const avgRating = Math.round(
    (campingRatings.renhold + campingRatings.fasiliteter + campingRatings.sanitaer + campingRatings.ro + campingRatings.prisVerdi) / 5
  );
  const reviewData = {
    spotId,
    spotName: spotName || '',
    userId: user.uid,
    userName: user.displayName || i18n.t('firebase.anonymous'),
    userPhoto: user.photoURL,
    rating: avgRating,
    comment,
    photoUrl: '',
    photoUrls: [],
    videoUrl: '',
    createdAt: Date.now(),
    campingRatings,
  };
  const reviewRef = doc(collection(db, 'reviews'));
  await setDoc(reviewRef, cleanFirestoreData(reviewData));
};

// Route Reviews
export const subscribeToRouteReviews = (routeId: string, callback: (reviews: any[]) => void) => {
  const q = query(collection(db, "reviews"), where("routeId", "==", routeId), orderBy("createdAt", "desc"));
  let fallbackUnsub: (() => void) | null = null;
  const mainUnsub = onSnapshot(
    q,
    (snapshot) => {
      const reviews = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      callback(reviews);
    },
    (error) => {
      if (error.code === 'failed-precondition') {
        if (!fallbackUnsub) {
          const fallbackQ = query(collection(db, "reviews"), where("routeId", "==", routeId));
          fallbackUnsub = onSnapshot(
            fallbackQ,
            (snapshot) => {
              const reviews = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a: any, b: any) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));
              callback(reviews);
            },
            () => {}
          );
        }
      }
    }
  );
  return () => {
    mainUnsub();
    if (fallbackUnsub) fallbackUnsub();
  };
};

export const addRouteReview = async (
  routeId: string,
  user: { uid: string; displayName: string | null; photoURL: string | null; activeVehicleCategory?: string; activeVehicleLength?: number; activeVehicleEquipment?: { four_wheel_drive?: boolean } },
  rating: number,
  comment: string,
  photos?: File[],
  videoUrl?: string
) => {
  const photoUrls: string[] = [];
  if (photos && photos.length > 0) {
    const compressedPhotos = await compressImages(photos);
    for (const photo of compressedPhotos) {
      const imgRef = ref(storage, `reviews/routes/${routeId}/${Date.now()}_${photo.name}`);
      const snap = await uploadBytes(imgRef, photo);
      photoUrls.push(await getDownloadURL(snap.ref));
    }
  }
  const normalizedVideoUrl = videoUrl?.trim() || '';
  const reviewData = {
    routeId,
    userId: user.uid,
    userName: user.displayName || i18n.t('firebase.anonymous'),
    userPhoto: user.photoURL,
    rating,
    comment,
    photoUrl: photoUrls[0] || '',
    photoUrls,
    videoUrl: normalizedVideoUrl,
    createdAt: Date.now(),
    ...(user.activeVehicleCategory ? {
      vehicleCategory: user.activeVehicleCategory,
      vehicleLength: user.activeVehicleLength || undefined,
      vehicleIs4wd: user.activeVehicleEquipment?.four_wheel_drive || undefined,
    } : {}),
  };
  const reviewRef = doc(collection(db, "reviews"));
  await setDoc(reviewRef, cleanFirestoreData(reviewData));
};

// Admin & Moderation
export const suggestDeletion = async (spotId: string, userId: string, reason: string, userName?: string | null) => {
  await setDoc(doc(collection(db, "deletion_suggestions")), {
    spotId,
    userId,
    ...(userName ? { userName } : {}),
    reason,
    status: 'pending',
    timestamp: serverTimestamp()
  });
};

export const fetchDeletionSuggestions = async (): Promise<DeletionSuggestion[]> => {
  const q = query(collection(db, "deletion_suggestions"), where("status", "==", "pending"));
  const snapshot = await getDocs(q);
  // Need to fetch spot details to show name/location
  const suggestions: DeletionSuggestion[] = [];
  for (const d of snapshot.docs) {
    const data = d.data();
    const spotDoc = await getDoc(doc(db, "cached_spots", data.spotId));
    if (spotDoc.exists()) {
      const spot = spotDoc.data() as CampingSpot;
      suggestions.push({
        id: d.id,
        spotId: data.spotId,
        userId: data.userId,
        userName: data.userName || null,
        reason: data.reason,
        status: data.status,
        timestamp: data.timestamp,
        spotName: spot.name,
        location: spot.location
      });
    }
  }
  return suggestions;
};

export const resolveDeletionSuggestion = async (suggestion: DeletionSuggestion, action: 'accept' | 'reject') => {
  const batch = writeBatch(db);
  const suggestionRef = doc(db, "deletion_suggestions", suggestion.id);
  batch.update(suggestionRef, { status: action });

  if (action === 'accept') {
    const spotRef = doc(db, "cached_spots", suggestion.spotId);
    batch.update(spotRef, { isInvalid: true, invalidReason: i18n.t('firebase.deleted_after_report') });
  }

  await batch.commit();
};

export const suggestCategoryChange = async (
  spotId: string, userId: string, userName: string,
  oldName: string, newName: string,
  oldDescription: string, newDescription: string,
  newSpotType: string, newSuitabilityColor: string, newFeature: string,
  videoUrl?: string, website?: string
) => {
  await setDoc(doc(collection(db, "category_suggestions")), cleanFirestoreData({
    spotId, userId, userName, oldName, newName, oldDescription, newDescription,
    newSpotType, newSuitabilityColor, newFeature,
    videoUrl, website,
    status: 'pending', createdAt: serverTimestamp()
  }));
};

export const fetchCategorySuggestions = async (): Promise<CategorySuggestion[]> => {
  const q = query(collection(db, "category_suggestions"), where("status", "==", "pending"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as CategorySuggestion));
};

export const resolveCategorySuggestion = async (
  suggestion: CategorySuggestion,
  action: 'approve' | 'reject',
  commonFeatures: string[]
) => {
  const batch = writeBatch(db);
  const suggRef = doc(db, "category_suggestions", suggestion.id);
  batch.update(suggRef, { status: action });

  if (action === 'approve') {
    const spotRef = doc(db, "cached_spots", suggestion.spotId);
    const spotSnap = await getDoc(spotRef);
    if (spotSnap.exists()) {
      const spot = spotSnap.data() as CampingSpot;
      // Remove old category features
      let features = (spot.features || []).filter(f => !commonFeatures.includes(f));
      // Add new feature
      features.push(suggestion.newFeature);
      // Ensure BRUKERBIDRAG is present
      if (!features.includes("BRUKERBIDRAG")) features.push("BRUKERBIDRAG");

      batch.update(spotRef, cleanFirestoreData({
        name: suggestion.newName,
        description: suggestion.newDescription,
        spotType: suggestion.newSpotType,
        suitabilityColor: suggestion.newSuitabilityColor,
        features: features,
        sourceLevel: 4
      }));
    }
  }
  await batch.commit();
};

// Tester & Subscriptions
export const createTesterRequest = async (user: AppUser, motivation: string) => {
  if (!user.email) throw new Error(i18n.t('firebase.errors.missing_email'));

  const requestData: Omit<TesterRequest, 'id'> = {
    userId: user.uid,
    userName: user.displayName || i18n.t('firebase.unknown_user'),
    userEmail: user.email,
    motivation,
    status: 'pending',
    createdAt: serverTimestamp()
  };

  await setDoc(doc(collection(db, "tester_requests")), cleanFirestoreData(requestData));
};

// Offboarding survey — lagres når en bruker sier opp abonnement eller sletter konto.
// Brukes for å kartlegge hvorfor de forlater oss og hva som skulle til for å beholde dem.
export type OffboardingSurveyType = 'cancellation' | 'deletion';
export const submitOffboardingSurvey = async (
  user: AppUser | null,
  payload: {
    type: OffboardingSurveyType;
    reason?: string;
    wouldReturn?: string;
    comment?: string;
    provider?: string;
  }
) => {
  if (!user?.uid) throw new Error(i18n.t('firebase.errors.not_logged_in', 'Du må være logget inn.'));

  const data = {
    userId: user.uid,
    userEmail: user.email || null,
    userName: user.displayName || null,
    type: payload.type,
    reason: payload.reason || null,
    wouldReturn: payload.wouldReturn || null,
    comment: (payload.comment || '').trim() || null,
    subscriptionProvider: payload.provider || null,
    appLanguage: i18n.language || null,
    platform: Capacitor.getPlatform(),
    createdAt: serverTimestamp(),
  };

  await setDoc(doc(collection(db, "offboarding_surveys")), cleanFirestoreData(data));
};

// Offboarding survey-svar lest av admin (Firestore-regel: allow read if isAdmin()).
export interface OffboardingSurvey {
  id: string;
  type: OffboardingSurveyType | string;
  reason: string | null;
  wouldReturn: string | null;
  comment: string | null;
  subscriptionProvider: string | null;
  appLanguage: string | null;
  platform: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  createdAt: number | null; // epoch ms
  source: string | null;    // 'apple_webhook' | 'stripe_webhook' = auto-detektert; ellers ekte survey-svar
}

export const fetchOffboardingSurveys = async (max = 2000): Promise<OffboardingSurvey[]> => {
  const q = query(collection(db, "offboarding_surveys"), orderBy("createdAt", "desc"), limit(max));
  const snapshot = await getDocs(q);
  const toMs = (c: any): number | null => {
    if (!c) return null;
    if (typeof c.toMillis === 'function') return c.toMillis();
    if (typeof c.seconds === 'number') return c.seconds * 1000;
    return null;
  };
  return snapshot.docs.map(d => {
    const data = d.data() as any;
    return {
      id: d.id,
      type: data.type || 'cancellation',
      reason: data.reason ?? null,
      wouldReturn: data.wouldReturn ?? null,
      comment: data.comment ?? null,
      subscriptionProvider: data.subscriptionProvider ?? null,
      appLanguage: data.appLanguage ?? null,
      platform: data.platform ?? null,
      userId: data.userId ?? null,
      userEmail: data.userEmail ?? null,
      userName: data.userName ?? null,
      createdAt: toMs(data.createdAt),
      source: data.source ?? null,
    };
  });
};

export const fetchTesterRequests = async (): Promise<TesterRequest[]> => {
  const q = query(collection(db, "tester_requests"), where("status", "==", "pending"));
  const snapshot = await getDocs(q);

  const getSortValue = (createdAt: any): number => {
    if (!createdAt) return 0;
    if (typeof createdAt === 'number') return createdAt;
    if (createdAt?.toMillis && typeof createdAt.toMillis === 'function') return createdAt.toMillis();
    if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
    return 0;
  };

  return snapshot.docs
    .map(d => ({ ...(d.data() as Omit<TesterRequest, 'id'>), id: d.id } as TesterRequest))
    .sort((a, b) => getSortValue((b as any).createdAt) - getSortValue((a as any).createdAt));
};

export const resolveTesterRequest = async (requestId: string, status: 'approved' | 'rejected', reason?: string, validDays?: number) => {
  const resolveRequest = httpsCallable(functions, 'resolveTesterRequestAdmin');
  await resolveRequest({ requestId, status, reason: reason || undefined, validDays });
};

export const endTesterProfile = async (targetUid?: string) => {
  const callable = httpsCallable(functions, 'endTesterProfile');
  const result = await callable({ targetUid });
  return result.data as { ok: true; uid: string; subscriptionStatus: 'free' | 'paid' };
};

export const promoteUserToTester = async (uid: string, validDays: number = 90): Promise<void> => {
  const callable = httpsCallable(functions, 'promoteToTester');
  await callable({ targetUid: uid, validDays });
};

export const recordUserCountryRequest = async (uid: string, countryKey: string) => {
  if (!uid || !countryKey) return;
  const normalizedKey = countryKey.toLowerCase();
  const userRef = doc(db, "users", uid);
  await setDoc(userRef, {
    requestedCountries: arrayUnion(normalizedKey)
  }, { merge: true });
};

export const fetchAllUsers = async (limitCount = 500): Promise<AppUser[]> => {
  const q = query(collection(db, "users"), orderBy("creationTimestamp", "desc"), limit(limitCount));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ ...(d.data() as Omit<AppUser, 'uid'>), uid: d.id } as AppUser));
};

export const fetchApprovedTesters = async (): Promise<TesterProfile[]> => {
  const q = query(collection(db, "users"), where("isTester", "==", true));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => {
    const data = d.data() as any;
    return {
      uid: d.id,
      displayName: data.displayName || null,
      email: data.email || null,
      photoURL: data.photoURL || null,
      isTester: !!data.isTester,
      platform: data.platform || undefined,
      tester_joined_at: data.tester_joined_at || null,
      feedback_count: data.feedback_count || 0,
      social_promo_count: data.social_promo_count || 0,
      tester_valid_until: data.tester_valid_until || null,
      requestedCountries: Array.isArray(data.requestedCountries) ? data.requestedCountries : undefined,
    };
  });
};

export const fetchTesterThreadsForAdmin = async (): Promise<TesterThread[]> => {
  const snapshot = await getDocs(collection(db, "tester_threads"));
  const getSortValue = (createdAt: any): number => {
    if (!createdAt) return 0;
    if (createdAt?.toMillis && typeof createdAt.toMillis === 'function') return createdAt.toMillis();
    if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
    return 0;
  };
  return snapshot.docs
    .map(d => ({ ...(d.data() as Omit<TesterThread, 'id'>), id: d.id } as TesterThread))
    .sort((a, b) => getSortValue(b.lastMessageAt) - getSortValue(a.lastMessageAt));
};

export const subscribeToTesterThreadsForAdmin = (callback: (threads: TesterThread[]) => void) => {
  const getSortValue = (createdAt: any): number => {
    if (!createdAt) return 0;
    if (createdAt?.toMillis && typeof createdAt.toMillis === 'function') return createdAt.toMillis();
    if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
    return 0;
  };

  return onSnapshot(collection(db, "tester_threads"), (snapshot) => {
    const threads = snapshot.docs
      .map(d => ({ ...(d.data() as Omit<TesterThread, 'id'>), id: d.id } as TesterThread))
      .sort((a, b) => getSortValue(b.lastMessageAt) - getSortValue(a.lastMessageAt));
    callback(threads);
  }, () => callback([]));
};

export const subscribeToTesterMessages = (testerUid: string, callback: (messages: TesterMessage[]) => void) => {
  const q = query(collection(db, "tester_threads", testerUid, "messages"), orderBy("createdAtMs", "asc"));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(d => ({ ...(d.data() as Omit<TesterMessage, 'id'>), id: d.id } as TesterMessage)));
  }, (err) => {
    console.error("[TesterMessages] onSnapshot error for", testerUid, err);
    // Fallback: try without ordering
    const q2 = query(collection(db, "tester_threads", testerUid, "messages"));
    onSnapshot(q2, (snap) => {
      const msgs = snap.docs.map(d => ({ ...(d.data() as Omit<TesterMessage, 'id'>), id: d.id } as TesterMessage));
      msgs.sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
      callback(msgs);
    }, () => {});
  });
};

export const translateTesterMessage = async (text: string, targetLang: string): Promise<string> => {
  const callable = httpsCallable<{ text: string; targetLang: string }, { translatedText: string }>(functions, 'translateMessage');
  const result = await callable({ text, targetLang });
  return result.data.translatedText;
};

export const getTesterLanguage = async (testerUid: string): Promise<string | null> => {
  const userDoc = await getDoc(doc(db, 'users', testerUid));
  if (!userDoc.exists()) return null;
  return (userDoc.data().language as string) || null;
};

export const sendTesterMessage = async (
  testerUid: string,
  sender: AppUser,
  senderRole: 'admin' | 'tester',
  text: string,
  imageFiles: File[],
  category?: 'bug' | 'improvement' | 'content' | 'other',
  testerMeta?: { testerName?: string; testerEmail?: string; testerPhotoURL?: string | null },
  translation?: { translatedText: string; translatedTo: string }
) => {
  const trimmed = text.trim();
  if (!trimmed && imageFiles.length === 0) return { skipped: [] };

  // compressImagesDetailed, ikke compressImages: den korte varianten svelger filer
  // den ikke får komprimert. Da ble det skrevet en melding med tom tekst OG tomme
  // imageUrls — en blank boble hos mottaker, uten at avsenderen fikk vite noe.
  const { files: compressedImageFiles, skipped } = await compressImagesDetailed(imageFiles);
  if (!trimmed && compressedImageFiles.length === 0) {
    throw new UnsendableImagesError(skipped);
  }

  const imageUrls: string[] = [];
  for (const file of compressedImageFiles) {
    const fileName = `${sender.uid}_${Date.now()}_${file.name}`;
    const imageRef = ref(storage, `tester_messages/${testerUid}/${fileName}`);
    const snap = await uploadBytes(imageRef, file);
    imageUrls.push(await getDownloadURL(snap.ref));
  }

  const threadRef = doc(db, "tester_threads", testerUid);
  const messageRef = doc(collection(db, "tester_threads", testerUid, "messages"));

  await setDoc(threadRef, cleanFirestoreData({
    testerUid,
    testerName: testerMeta?.testerName || sender.displayName || 'Tester',
    testerEmail: testerMeta?.testerEmail || sender.email || '',
    ...(testerMeta?.testerPhotoURL !== undefined && { testerPhotoURL: testerMeta.testerPhotoURL }),
  }), { merge: true });

  await setDoc(messageRef, cleanFirestoreData({
    testerUid,
    senderId: sender.uid,
    senderName: sender.displayName || (senderRole === 'admin' ? 'Admin' : 'Tester'),
    senderRole,
    category: category || null,
    text: trimmed,
    imageUrls,
    createdAt: serverTimestamp(),
    createdAtMs: Date.now(),
    ...(translation && { translatedText: translation.translatedText, translatedTo: translation.translatedTo }),
  }));

  // Delvis sending: teksten gikk gjennom, men ett eller flere vedlegg ble ikke med.
  // Kallstedet må si fra — ellers tror avsenderen at bildet fulgte med.
  return { skipped };
};

export const markTesterThreadAsRead = async (testerUid: string, role: 'admin' | 'tester') => {
  const threadRef = doc(db, "tester_threads", testerUid);
  const update = role === 'admin'
    ? { unreadForAdmin: 0, lastReadByAdmin: Date.now() }
    : { unreadForTester: 0, lastReadByTester: Date.now() };
  await setDoc(threadRef, update, { merge: true });
};

export const subscribeToTesterThread = (testerUid: string, callback: (thread: TesterThread | null) => void) => {
  return onSnapshot(doc(db, "tester_threads", testerUid), (snap) => {
    callback(snap.exists() ? { ...(snap.data() as Omit<TesterThread, 'id'>), id: snap.id } as TesterThread : null);
  }, () => callback(null));
};

export const createStripeCheckoutSession = async (returnUrl?: string) => {
  const createCheckout = httpsCallable(functions, 'createStripeCheckout');
  const result = await createCheckout({ returnUrl });
  return result.data as { sessionId: string; url: string };
};

export const createStripePremiumCheckoutSession = async (returnUrl?: string) => {
  const createCheckout = httpsCallable(functions, 'createStripePremiumCheckout');
  const result = await createCheckout({ returnUrl });
  return result.data as { sessionId: string; url: string };
};

export const createStripeBillingPortalSession = async (returnUrl?: string) => {
  const openPortal = httpsCallable(functions, 'createStripeBillingPortal');
  const result = await openPortal({ returnUrl });
  return result.data as { url: string };
};

export const verifyStripeCheckout = async (sessionId: string) => {
  const verify = httpsCallable(functions, 'verifyStripeCheckout');
  const result = await verify({ sessionId });
  return result.data as { status: string; plan?: string; paymentStatus?: string };
};

export const createVippsAgreementSession = async (returnUrl?: string, phoneNumber?: string) => {
  const createAgreement = httpsCallable(functions, 'createVippsAgreement');
  const result = await createAgreement({ returnUrl, phoneNumber });
  return result.data as { agreementId: string; vippsConfirmationUrl: string };
};

export const createVippsPremiumAgreementSession = async (returnUrl?: string, phoneNumber?: string) => {
  const createAgreement = httpsCallable(functions, 'createVippsPremiumAgreement');
  const result = await createAgreement({ returnUrl, phoneNumber });
  return result.data as { agreementId: string; vippsConfirmationUrl: string };
};

export const stopVippsAgreementSession = async () => {
  const stopAgreement = httpsCallable(functions, 'stopVippsAgreement');
  const result = await stopAgreement({});
  return result.data as { ok: boolean; paidUntil: number | null };
};

// Vipps safety net — verifiserer avtalestatus direkte mot Vipps når brukeren
// returnerer fra Vipps (?vipps_agreement=pending), uavhengig av webhook.
export const reconcileVippsAgreement = async () => {
  const reconcile = httpsCallable(functions, 'reconcileVippsAgreement');
  const result = await reconcile({});
  return result.data as { status: string; plan?: string; vippsStatus?: string };
};

export const getPaymentHistory = async () => {
  const callable = httpsCallable(functions, 'getPaymentHistory');
  const result = await callable({});
  return result.data as {
    transactions: Array<{ id: string; date: number; amount: number; currency: string; status: string; description: string; provider: 'stripe' | 'vipps' | 'apple'; receiptUrl: string | null }>;
    provider: string | null;
    subscriptionStatus: string;
  };
};

// ── Inntektsrapportering (admin) ────────────────────────────────────
export interface RevenueSubscriber {
  uid: string;
  name: string | null;
  email: string | null;
  provider: 'stripe' | 'vipps' | 'apple' | null;
  plan: string | null;
  status: 'paid' | 'trial' | 'sandbox' | 'tester' | 'test' | 'churned' | 'family_shared';
  active: boolean;
  familyShared?: boolean;
  appleOwnership?: 'purchased' | 'family_shared' | 'unknown' | null;
  appleProceedsUsd?: number | null;
  startDate: number | null;
  lastPaymentDate: number | null;
  totalPaidNok: number;
  txCount: number;
  unknownFx: boolean;
  currentPeriodEnd: number | null;
}

export interface RevenueReport {
  counts: {
    activePaying: number; trial: number; free: number; total: number; familyShared: number;
    stripePaid: number; vippsPaid: number; applePaid: number;
  };
  estimatedMrr: number;
  appleRevenue?: { grossUsd: number; proceedsUsd: number };
  last30Revenue: number;
  monthlyPrice: number;
  premiumPrice: number;
  revenue: {
    monthly: Array<{ month: string; stripe: number; vipps: number; apple: number; total: number; count: number }>;
    yearly: Array<{ year: string; stripe: number; vipps: number; apple: number; total: number; count: number }>;
  };
  weekly: Array<{ week: string; created: number; cancelled: number }>;
  newUsersByMonth?: Array<{ month: string; count: number }>;
  subscribers: RevenueSubscriber[];
  transactionCount: number;
  generatedAt: number;
}

export const getRevenueReport = async () => {
  const callable = httpsCallable(functions, 'getRevenueReport');
  const result = await callable({});
  return result.data as RevenueReport;
};

export interface SubscriberPayment {
  id: string; date: number; amount: number; currency: string; amountNok: number | null;
  status: string; type: string; description: string; provider: 'stripe' | 'vipps' | 'apple'; source: string;
}

export const getSubscriberPayments = async (uid: string) => {
  const callable = httpsCallable(functions, 'getSubscriberPayments');
  const result = await callable({ uid });
  return result.data as { transactions: SubscriberPayment[] };
};

export const runRevenueBackfill = async () => {
  const callable = httpsCallable(functions, 'backfillRevenueData');
  const result = await callable({});
  return result.data as {
    ok: boolean; scannedUsers: number; txCount: number; eventCount: number;
    stripeUsers: number; vippsUsers: number; appleUsers: number;
    cancelledReconstructed: number; errorCount: number; errors: string[];
  };
};

export const runRevenueReconciliation = async () => {
  const callable = httpsCallable(functions, 'reconcileRevenueNow');
  const result = await callable({});
  return result.data as {
    ok: boolean; appleChecked: number; appleFamilyShared: number; applePurchased: number;
    appleNoSub: number; appleErrors: number; txRecomputed: number;
    appleGrossUsd: number; appleProceedsUsd: number; ranAt: number;
  };
};

export const testVippsConnection = async () => {
  const test = httpsCallable(functions, 'testVippsConnection');
  const result = await test({});
  return result.data as { ok: boolean; error?: string; tokenType?: string; expiresIn?: number };
};

export const fetchSeoMetricsReport = async () => {
  const callable = httpsCallable(functions, 'getSeoMetricsReport');
  const result = await callable({});
  return result.data as {
    month: string;
    totals: { views: number; humanViews: number; botViews: number };
    topPages: Array<{
      page: string;
      county_slug: string;
      municipality_slug?: string;
      views: number;
      humanViews: number;
      botViews: number;
    }>;
    pageCount: number;
    sitemapUrl: string;
  };
};

export const runExternalSpotsSync = async (regionIds?: string[]) => {
  const callable = httpsCallable(functions, 'runExternalSpotsSync');
  const result = await callable({ regionIds: Array.isArray(regionIds) ? regionIds : [] });
  return result.data as {
    ok?: boolean;
    skipped?: boolean;
    reason?: string;
    counters?: {
      regions: number;
      fetchedNvdb: number;
      fetchedOsmAmenities: number;
      fetchedOsmCamp: number;
      totalFetched: number;
      totalWritten: number;
      errors: number;
    };
  };
};

export const analyzeVerifiedSpotVehicles = async (spotId: string) => {
  const callable = httpsCallable(functions, 'analyzeVerifiedSpotVehicles');
  const result = await callable({ spotId });
  return result.data as {
    ok?: boolean;
    skipped?: boolean;
    reason?: string;
    spotId?: string;
    tags?: string[];
    confidence?: Record<string, number>;
  };
};

export const aiAnalyzeSpotImage = async (imageBase64: string, mimeType: string): Promise<{ ok?: boolean; data?: { category?: string; title?: string; description?: string } }> => {
  const callable = httpsCallable(functions, 'aiAnalyzeSpotImage');
  const result = await callable({ imageBase64, mimeType });
  return result.data as { ok?: boolean; data?: { category?: string; title?: string; description?: string } };
};

export interface AiSuggestSpotContext {
  areaLabel?: string | null;
  elevation?: number | null;
  highway?: string | null;
  surface?: string | null;
  roadDistance?: number | null;
  nearbyAmenities?: Array<{
    type: string;
    label: string;
    name?: string | null;
    distanceM: number;
  }>;
}

export const aiSuggestSpotFromContext = async (input: {
  coords: { latitude: number; longitude: number };
  categoryId?: string;
  categoryLabel?: string;
  categoryIds?: string[];
  categoryLabels?: string[];
}): Promise<{ ok?: boolean; reason?: string; data?: { title?: string; description?: string; context?: AiSuggestSpotContext } }> => {
  const callable = httpsCallable(functions, 'aiSuggestSpotFromContext');
  const result = await callable({ ...input, language: i18n.language });
  return result.data as { ok?: boolean; reason?: string; data?: { title?: string; description?: string; context?: AiSuggestSpotContext } };
};

export interface AiCampsiteEnrichment {
  website?: string | null;
  phone?: string | null;
  opening_hours?: string | null;
  description?: string | null;
  rating?: number | null;
  ratingCount?: number | null;
  googleMapsUri?: string | null;
  placeId?: string | null;
  /** 'high' = trygt å auto-fylle; 'medium'/'low' = kun ved eksplisitt «Hent info». */
  confidence?: 'high' | 'medium' | 'low';
  matchedName?: string | null;
}

/**
 * Slår opp en kommersiell campingplass i Google Places og returnerer strukturerte
 * felt (telefon, åpningstider, nettside, beskrivelse) til autofyll i AddSpotModal.
 * Skriver ingenting — klienten fyller feltene som redigerbare AI-forslag, bruker
 * lagrer selv ved submit. Nettside-URL brukes som matche-signal for presist treff.
 */
export const aiEnrichCommercialCampsite = async (input: {
  name: string;
  coords: { latitude: number; longitude: number };
  website?: string;
}): Promise<{ ok?: boolean; reason?: string; data?: AiCampsiteEnrichment }> => {
  const callable = httpsCallable(functions, 'aiEnrichCommercialCampsite');
  const result = await callable(input);
  return result.data as { ok?: boolean; reason?: string; data?: AiCampsiteEnrichment };
};

/**
 * Reise-copilot: én samtale-tur. Sender hele historikken + brukerprofil, får AI-svar +
 * forhåndsvalg + oppdatert TripSpec. Ren request/respons (ingen streaming i Fase 1).
 * Premium — kaster HttpsError('permission-denied') for ikke-betalende.
 */
export const planTripWithCopilot = async (input: {
  messages: CopilotMessage[];
  profile?: Record<string, unknown>;
}): Promise<CopilotTurnResponse> => {
  const callable = httpsCallable(functions, 'planTripWithCopilot', { timeout: 60000 });
  const result = await callable(input);
  return result.data as CopilotTurnResponse;
};

/**
 * Markerer at en plass nettopp ble aktivt beriket (bilde/rating) av en bruker.
 * Skriver kun til cached_spots — den bruker-skrivbare speilingen (spots/ er admin/eier-only).
 * Driver "Nylig bekreftet" i news-feeden. Ikke-kritisk: feiler stille hvis doc mangler.
 */
export const markSpotConfirmed = async (
  spotId: string,
  confirmedBy?: { uid: string; displayName: string | null; photoURL: string | null },
): Promise<void> => {
  try {
    const cachedRef = doc(db, 'cached_spots', spotId);
    const update: Record<string, any> = { lastConfirmedAt: Date.now() };
    if (confirmedBy?.uid) {
      // Denormaliser bekrefteren så feeden viser hvem som bekreftet (ikke skaperen).
      // Sett foto til null (ikke undefined) når det mangler, så en tidligere
      // bekrefters bilde ikke blir hengende igjen.
      update.confirmedBy = confirmedBy.uid;
      update.confirmedByName = confirmedBy.displayName || i18n.t('firebase.anonymous');
      update.confirmedByPhoto = confirmedBy.photoURL || null;
    }
    await updateDoc(cachedRef, update);
  } catch { /* cached_spots kan mangle, eller regler — bevisst stille */ }
};

export const updateSpotPhotoUrls = async (spotId: string, newUrls: string[], markVerified = false): Promise<void> => {
  if (!spotId || newUrls.length === 0) return;
  const update: Record<string, any> = { photoUrls: arrayUnion(...newUrls) };
  if (markVerified) {
    update.isVerified = true;
    update.verifiedAt = Date.now();
  }
  update.updatedAt = Date.now();

  try {
    const cachedRef = doc(db, 'cached_spots', spotId);
    const cachedSnap = await getDoc(cachedRef);
    if (cachedSnap.exists() && !cachedSnap.data().ugcThumbnailUrl) {
      update.ugcThumbnailUrl = newUrls[0];
    }
    if (cachedSnap.exists()) {
      await updateDoc(cachedRef, update);
    } else {
      await setDoc(cachedRef, update, { merge: true });
    }
  } catch (err) {
    console.warn('[updateSpotPhotoUrls] cached_spots update failed', err);
  }

  try {
    const spotRef = doc(db, 'spots', spotId);
    await updateDoc(spotRef, update);
  } catch { /* spots collection might not contain doc */ }
};

export const aiAnalyzeSpot = async (spotData: any, isReanalysis = false, isStrictValidation = false) => {
  const callable = httpsCallable(functions, 'aiAnalyzeSpot');
  const result = await callable({ spotData, isReanalysis, isStrictValidation, language: i18n.language });
  return result.data as { ok?: boolean; reason?: string; data?: any };
};

export const enrichSpotFishing = async (latitude: number, longitude: number, spotId?: string) => {
  const callable = httpsCallable(functions, 'enrichSpotFishing');
  const result = await callable({ latitude, longitude, spotId });
  return result.data as {
    totalObservations: number;
    scoreLabel: string;
    scoreLevel: number;
    speciesBreakdown: { species: string; count: number }[];
    lastUpdated: string;
  };
};

// --- Server-side batch scanning & analysis ---

export const scanAreaServer = async (bounds: { latMin: number; lonMin: number; latMax: number; lonMax: number }, userTriggered?: boolean) => {
  const callable = httpsCallable(functions, 'scanAreaBatch', { timeout: 300000 });
  const result = await callable({ bounds, userTriggered: !!userTriggered });
  return result.data as {
    spots: any[];
    stats: { total: number; enriched: number; filtered: number; kept: number };
  };
};

export const deepAnalyzeServer = async (spotIds: string[]) => {
  const callable = httpsCallable(functions, 'deepAnalyzeBatch', { timeout: 300000 });
  const result = await callable({ spotIds });
  return result.data as {
    analyzed: number;
    kept: number;
    removed: number;
    results: Array<{ id: string; status: string; score?: number; reason?: string; aiResult?: any }>;
  };
};

export const aiGenerateRouteMetadata = async (
  startCoord: [number, number],
  endCoord: [number, number],
  distanceKm: number,
  waypoints?: [number, number][],
  elevationProfile?: ElevationPoint[],
  waypointNames?: string[]
) => {
  const callable = httpsCallable(functions, 'aiGenerateRouteMetadata');
  const result = await callable({ startCoord, endCoord, distanceKm, waypoints, elevationProfile, waypointNames });
  return result.data as { ok?: boolean; reason?: string; data?: { title?: string; description?: string; startPlace?: string; endPlace?: string } };
};

export const translateRouteText = async (routeId: string, targetLang: string) => {
  const callable = httpsCallable(functions, 'translateRouteText');
  const result = await callable({ routeId, targetLang });
  return result.data as { ok?: boolean; data?: { name: string; description: string }; cached?: boolean };
};

export const translateSpotContent = async (
  spotId: string,
  targetLang: string,
  spotName: string,
  spotDescription: string,
  reviewComment?: string
) => {
  const callable = httpsCallable(functions, 'translateSpotContent');
  const result = await callable({ spotId, targetLang, spotName, spotDescription, reviewComment: reviewComment || '' });
  return result.data as { ok?: boolean; data?: { spotName: string; spotDescription: string; reviewComment: string }; cached?: boolean };
};

export const analyzeRouteServer = async (routeId: string, layers?: string[]) => {
  const callable = httpsCallable(functions, 'analyzeRoute', { timeout: 320000 });
  const result = await callable({ routeId, ...(layers ? { layers } : {}) });
  return result.data as { ok?: boolean; stats?: any; wikiSpots?: number };
};

// Subscribe to real-time route document updates (for progressive analysis)
export const subscribeToRouteUpdates = (routeId: string, callback: (data: any) => void) => {
  const routeRef = doc(db, 'routes', routeId);
  return onSnapshot(routeRef, (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    }
  }, () => {});
};

export const aiAnalyzeExternalLink = async (url: string) => {
  const callable = httpsCallable(functions, 'aiAnalyzeExternalLink');
  const result = await callable({ url });
  return result.data as { ok?: boolean; reason?: string; data?: { title?: string; type?: 'article' | 'video' | 'image' | 'other' } };
};

export const aiGenerateTrailName = async (input: {
  spotName?: string;
  municipality?: string;
  county?: string;
  lengthKm?: number;
  difficulty?: string;
  trailType?: string;
}) => {
  const callable = httpsCallable(functions, 'aiGenerateTrailName');
  const result = await callable({ input });
  return result.data as { ok?: boolean; reason?: string; data?: { name?: string } };
};

export const aiGenerateSpotShareSummary = async (input: {
  spotName?: string;
  highway?: string;
  surfaceType?: string;
  nearestHouse?: number;
  score?: number;
  features?: string[];
  municipality?: string;
  county?: string;
}) => {
  const callable = httpsCallable(functions, 'aiGenerateSpotShareSummary');
  const result = await callable({ input, language: i18n.language });
  return result.data as { ok?: boolean; reason?: string; data?: { summary?: string } };
};

export const aiGenerateSpotName = async (input: {
  address?: string;
  municipality?: string;
  county?: string;
  highway?: string;
  surfaceType?: string;
  features?: string[];
}) => {
  const callable = httpsCallable(functions, 'aiGenerateSpotName');
  const result = await callable({ input });
  return result.data as { ok?: boolean; reason?: string; data?: { name?: string } };
};

export const aiClassifyWaypoints = async (waypoints: { name: string; description?: string }[]) => {
  const callable = httpsCallable(functions, 'aiClassifyWaypoints');
  const result = await callable({ waypoints });
  return result.data as { ok?: boolean; reason?: string; data?: { icons?: string[] } };
};

export const aiClassifyRouteSuitability = async (routeData: {
  name: string; description: string; distanceKm?: number; elevationGain?: number;
  maxElevation?: number; creatorVehicleCategory?: string; creatorVehicleLength?: number;
  creatorVehicleIs4wd?: boolean; region?: string;
}) => {
  const callable = httpsCallable(functions, 'aiClassifyRouteSuitability');
  const result = await callable(routeData);
  return result.data as { ok?: boolean; reason?: string; data?: {
    suitableCategories?: string[]; terrainTypes?: string[]; requires4wd?: boolean;
    maxRecommendedLengthCm?: number; suitabilityNote?: string;
  } };
};

export const batchClassifyRoutes = async () => {
  const callable = httpsCallable(functions, 'batchClassifyRoutes', { timeout: 540000 });
  const result = await callable({});
  return result.data as { ok?: boolean; data?: { total: number; classified: number; failed: number } };
};

export const subscribeToExternalSyncStatus = (callback: (status: any | null) => void) => {
  return onSnapshot(doc(db, "system", "external_sync_status"), (docSnap) => {
    callback(docSnap.exists() ? docSnap.data() : null);
  }, () => callback(null));
};

// ─── Brukerinnboks (admin → bruker) ──────────────────────────────────────────

export interface FoundUser {
  uid: string;
  displayName: string | null;
  email: string;
  photoURL: string | null;
  isTester: boolean;
}

/** Søk etter bruker på e-postadresse (kun admin). */
export const findUserByEmail = async (email: string): Promise<FoundUser> => {
  const callable = httpsCallable(functions, 'findUserByEmail');
  const result = await callable({ email });
  return result.data as FoundUser;
};

/** Send innboks-melding til en bruker (kun admin). */
export const sendUserMessage = async (opts: {
  userId: string;
  subject: string;
  body: string;
  sendEmail: boolean;
}): Promise<{ success: boolean; messageId: string }> => {
  const callable = httpsCallable(functions, 'sendUserMessage');
  const result = await callable(opts);
  return result.data as { success: boolean; messageId: string };
};

// ─── Feature Usage Tracking ────────────────────────────────────────────────

export type FeatureKey =
  | 'map_view' | 'route_plan' | 'search' | 'spot_visit' | 'spot_visit_retro'
  | 'review_write' | 'favorite_add' | 'offline_download'
  | 'share' | 'trip_log' | 'article_read'
  | 'collab_map_view' | 'filter_change' | 'flyover_3d' | 'follow_route' | 'navigate_to_route'
  | 'navigate_to_spot' | 'route_plan_navigate' | 'navigate_from_builder';

const _usageThrottleMap: Record<string, number> = {};
const USAGE_THROTTLE_MS = 60_000;

export const logFeatureUsage = (feature: FeatureKey): void => {
  try {
    if (!auth.currentUser) return;
    const now = Date.now();
    const last = _usageThrottleMap[feature] || 0;
    if (now - last < USAGE_THROTTLE_MS) return;
    _usageThrottleMap[feature] = now;

    const dateKey = new Date().toISOString().split('T')[0];
    const docRef = doc(db, 'usage_stats', dateKey);
    setDoc(docRef, { [feature]: increment(1) }, { merge: true }).catch(err => {
      console.debug('usage_stats write failed', err);
    });
  } catch {
    // Non-critical – silently ignore
  }
};

// ─── Region View Tracking (aggregated, privacy-safe) ────────────────────────

const _regionViewThrottleMap: Record<string, number> = {};
const REGION_VIEW_THROTTLE_MS = 60_000;
let _lastLoggedCounty: string | null = null;

export const logRegionView = (county: string): void => {
  try {
    if (!auth.currentUser) return;
    if (!county) return;

    const now = Date.now();
    const last = _regionViewThrottleMap[county] || 0;
    if (county === _lastLoggedCounty && now - last < REGION_VIEW_THROTTLE_MS) return;

    _regionViewThrottleMap[county] = now;
    _lastLoggedCounty = county;

    const dateKey = new Date().toISOString().split('T')[0];
    const docRef = doc(db, 'region_views', dateKey);
    setDoc(docRef, { [county]: increment(1) }, { merge: true }).catch(() => {});
  } catch {
    // Non-critical
  }
};

// ─── Search Area Tracking (aggregated, privacy-safe) ────────────────────────
// Hvilket fylke brukeren faktisk søkte seg til (valgt søketreff). Aggregert per
// dato/fylke — ingen søketekst eller bruker-data lagres. Speiler logRegionView.

const _searchAreaThrottleMap: Record<string, number> = {};
const SEARCH_AREA_THROTTLE_MS = 60_000;

export const logSearchArea = (county: string): void => {
  try {
    if (!auth.currentUser) return;
    if (!county) return;

    const now = Date.now();
    const last = _searchAreaThrottleMap[county] || 0;
    if (now - last < SEARCH_AREA_THROTTLE_MS) return;
    _searchAreaThrottleMap[county] = now;

    const dateKey = new Date().toISOString().split('T')[0];
    const docRef = doc(db, 'search_areas', dateKey);
    setDoc(docRef, { [county]: increment(1) }, { merge: true }).catch(() => {});
  } catch {
    // Non-critical
  }
};
